import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  getFirestore, 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  setDoc, 
  updateDoc, 
  serverTimestamp, 
  addDoc, 
  getDoc, 
  deleteDoc, 
  writeBatch
} from 'firebase/firestore';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import UserContext from '../../context/UserContext';
import TourContext from '../../context/TourContext';
import TopBar from '../TopBar';
import LeftMenu from '../LeftMenu';
import RightMenu from '../RightMenu';
import { Calendar } from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faImage, 
  faTrash, 
  faCalendar, 
  faMapMarkerAlt, 
  faUsers, 
  faCheck, 
  faSearch, 
  faTimes, 
  faLock, 
  faLockOpen,
  faBuilding,
  faMusic,
  faUserTie,
  faCog,
  faToggleOn,
  faToggleOff,
  faExclamationTriangle,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import styles from './CreateTour.module.css';
import defaultAvatar from '../../assets/Images/defaultUserProfilePicture.png';
import verifiedBadge from '../../assets/Images/profileverifiedtick.png';
import CreateTourMapSearch from './CreateTourMapSearch';
import { liteClient } from 'algoliasearch/lite';
import { 
  sendEventInvitationNotification, 
  sendEventRemovalNotification, 
  createNotification, 
  createBandNotification,
  NOTIFICATION_TYPES,
} from '../../utils/NotificationService';
import { incrementUserStats, decrementUserStats } from '../../utils/actions/userActions';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import defaultVenueImage from '../../assets/Images/defaulthotelpicture.png';
import defaultBandPic from '../../assets/Images/defaultBandPic.png';
import defaultOrgImage from '../../assets/Images/defaultOrganizationLogo.png';

const STEPS = {
  EVENT_TYPE: 0,             // New initial step to choose Tour or Show
  TOUR_DETAILS: 1,
  SELECT_DATES: 2,
  VENUE_DETAILS: 3,
  BAND_SELECTION: 4,
  ARTIST_SELECTION: 5,
  CREW_SELECTION: 6,
  MANAGER_SELECTION: 7,      // New step for Manager selection
  ORGANIZATION_SELECTION: 8, // New step for Organization selection
  PERMISSIONS: 9             // New step for permission settings
};

const PERMISSION_LEVELS = {
  FULL_CONTROL: 'fullControl',
  VIEW_ONLY: 'viewOnly',
  COLLAB: 'collab'
};

const CreateTour = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const tourData = location.state?.tourData;
  const isEditing = !!tourData;
  const { userData, setUserData } = useContext(UserContext);
  const { fetchEventsData, setSelectedEvent, setEvents, events, clearCache } = useContext(TourContext);
  const [currentStep, setCurrentStep] = useState(STEPS.EVENT_TYPE);
  const [loading, setLoading] = useState(false);
  
  // Event type state
  const [eventType, setEventType] = useState('tour'); // 'tour' or 'show'
  
  // Tour details states
  const [coverPhotoUrl, setCoverPhotoUrl] = useState('');
  const [eventName, setEventName] = useState(''); // Common field for tour/show name
  const [actName, setActName] = useState('');
  const [actType, setActType] = useState('');
  const [coverPhoto, setCoverPhoto] = useState(null);
  const [coverPhotoPreview, setCoverPhotoPreview] = useState(null);
  const [selectedDates, setSelectedDates] = useState([]);
  
  // Venue states
  const [venueResults, setVenueResults] = useState({});
  const [venueDetails, setVenueDetails] = useState({});
  const [searchingVenue, setSearchingVenue] = useState(false);
  const [selectedDateIndex, setSelectedDateIndex] = useState(0);
  const [venueSearchQuery, setVenueSearchQuery] = useState('');
  const [showMapSearch, setShowMapSearch] = useState(false);
  const [isSearchButtonPressed, setIsSearchButtonPressed] = useState(false);

  // Artist states
  const [selectedArtists, setSelectedArtists] = useState([]);
  const [artistResults, setArtistResults] = useState([]);
  const [searchingArtists, setSearchingArtists] = useState(false);
  const [artistDateAssignments, setArtistDateAssignments] = useState({});
  const [artistSearchQuery, setArtistSearchQuery] = useState('');
  const [artistPermissions, setArtistPermissions] = useState({});
  const [artistDateAvailability, setArtistDateAvailability] = useState({});

  // Crew states
  const [selectedCrew, setSelectedCrew] = useState([]);
  const [crewResults, setCrewResults] = useState([]);
  const [searchingCrew, setSearchingCrew] = useState(false);
  const [crewDateAssignments, setCrewDateAssignments] = useState({});
  const [crewSearchQuery, setCrewSearchQuery] = useState('');
  const [crewPermissions, setCrewPermissions] = useState({});
  const [crewDateAvailability, setCrewDateAvailability] = useState({});

  // Band states
  const [selectedBands, setSelectedBands] = useState([]);
  const [bandSearchQuery, setBandSearchQuery] = useState('');
  const [searchingBands, setSearchingBands] = useState(false);
  const [bandResults, setBandResults] = useState([]);
  const [bandDateAssignments, setBandDateAssignments] = useState({});
  const [bandPermissions, setBandPermissions] = useState({});
  const [bandDateAvailability, setBandDateAvailability] = useState({});
  
  // Manager states
  const [selectedManagers, setSelectedManagers] = useState([]);
  const [managerResults, setManagerResults] = useState([]);
  const [searchingManagers, setSearchingManagers] = useState(false);
  const [managerDateAssignments, setManagerDateAssignments] = useState({});
  const [managerSearchQuery, setManagerSearchQuery] = useState('');
  const [managerPermissions, setManagerPermissions] = useState({});
  const [managerDateAvailability, setManagerDateAvailability] = useState({});
  
  // Organization states
  const [selectedOrganizations, setSelectedOrganizations] = useState([]);
  const [organizationResults, setOrganizationResults] = useState([]);
  const [searchingOrganizations, setSearchingOrganizations] = useState(false);
  const [organizationDateAssignments, setOrganizationDateAssignments] = useState({});
  const [organizationSearchQuery, setOrganizationSearchQuery] = useState('');
  const [organizationPermissions, setOrganizationPermissions] = useState({});
  const [organizationDateAvailability, setOrganizationDateAvailability] = useState({});

  // State for pending invitations
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [confirmedDatesByUser, setConfirmedDatesByUser] = useState({});
  const [pendingDatesByUser, setPendingDatesByUser] = useState({});
  const [removedConfirmedDates, setRemovedConfirmedDates] = useState({});
  const [unselectedConfirmedDates, setUnselectedConfirmedDates] = useState({});
  
  // Status tracking states
  const [showVenueWarningModal, setShowVenueWarningModal] = useState(false);
  const [existingArtistAssignments, setExistingArtistAssignments] = useState({});
  const [existingCrewAssignments, setExistingCrewAssignments] = useState({});
  const [loadingMessage, setLoadingMessage] = useState('');

  // UI state for permissions
  const [showPermissionSettings, setShowPermissionSettings] = useState({});

  const [removedVenueCache, setRemovedVenueCache] = useState({});

  // State for user's assigned dates
  const [userAssignedDates, setUserAssignedDates] = useState({});
  const [showDateConflictModal, setShowDateConflictModal] = useState(false);
  const [conflictDetails, setConflictDetails] = useState(null);

  const [showPastDateModal, setShowPastDateModal] = useState(false);
const [pastDateToDeselect, setPastDateToDeselect] = useState(null);

const [activeMonth, setActiveMonth] = useState(new Date());

  // Initialize Algolia
  const client = liteClient("6DJNAZSX4P", "342932cbe26f7751ec87fa2b1ff32e4c");

  const db = getFirestore();

  // Default permission settings for each role
  const defaultPermissions = {
    fullControl: {
      canEditTourDetails: true,
      canEditSchedule: true,
      canAddAttachments: true,
      canAssignTasks: true,
      canEditSetlist: true,
      canManageGuestlist: true,
      canManageHotels: true,
      canManageTickets: true,
      canAssignUsers: true,
      canManageFinancials: true
    },
    viewOnly: {
      canEditTourDetails: false,
      canEditSchedule: false,
      canAddAttachments: false,
      canAssignTasks: false,
      canEditSetlist: false,
      canManageGuestlist: false,
      canManageHotels: false,
      canManageTickets: false,
      canAssignUsers: false,
      canManageFinancials: false
    },
    collab: {
      canEditTourDetails: false,
      canEditSchedule: true,
      canAddAttachments: true,
      canAssignTasks: true,
      canEditSetlist: true,
      canManageGuestlist: true,
      canManageHotels: true,
      canManageTickets: true,
      canAssignUsers: false,
      canManageFinancials: false
    }
  };

  // Default role-specific permissions
  const roleDefaultPermissions = {
    artist: {
      canEditTourDetails: false,
      canEditSchedule: false,
      canAddAttachments: true,
      canAssignTasks: false,
      canEditSetlist: true,
      canManageGuestlist: true,
      canManageHotels: false,
      canManageTickets: false,
      canAssignUsers: false,
      canManageFinancials: false
    },
    crew: {
      canEditTourDetails: false,
      canEditSchedule: true,
      canAddAttachments: true,
      canAssignTasks: true,
      canEditSetlist: false,
      canManageGuestlist: true,
      canManageHotels: true,
      canManageTickets: true,
      canAssignUsers: false,
      canManageFinancials: false
    },
    band: {
      canEditTourDetails: false,
      canEditSchedule: false,
      canAddAttachments: true,
      canAssignTasks: false,
      canEditSetlist: true,
      canManageGuestlist: true,
      canManageHotels: false,
      canManageTickets: false,
      canAssignUsers: false,
      canManageFinancials: false
    },
    manager: { ...defaultPermissions.collab },
    organization: { ...defaultPermissions.viewOnly }
  };

  useEffect(() => {
    document.title = isEditing ? 'Edit Tour | On Tour' : 'Create Tour | On Tour';
    
    return () => {
      document.title = 'On Tour';
    };
  }, [isEditing]);

  useEffect(() => {
    const checkLimit = async () => {
      const canCreateEvent = await checkEventLimit();
      if (!canCreateEvent) {
        alert(`You have reached the maximum number of ${eventType}s for your ${userData?.membershipTier} membership tier. Please upgrade to create more ${eventType}s.`);
        navigate('/crew');
      }
    };
    
    checkLimit();
  }, [userData, navigate, eventType]);

  // Load assigned dates for the current user
  useEffect(() => {
    const fetchUserAssignedDates = async () => {
      if (!userData || !userData.userId) return;
      
      try {
        const db = getFirestoreInstance();
        const assignedDatesRef = collection(db, 'users', userData.userId, 'assignedEventDates');
        const snapshot = await getDocs(assignedDatesRef);
        
        const assignedDatesMap = {};
        
        snapshot.forEach(doc => {
          const dateData = doc.data();
          const dates = dateData.dates || [];
          
          dates.forEach(dateInfo => {
            const date = dateInfo.date;
            assignedDatesMap[date] = {
              ...dateInfo,
              docId: doc.id
            };
          });
        });
        
        setUserAssignedDates(assignedDatesMap);
      } catch (error) {
        console.error('Error fetching assigned dates:', error);
      }
    };
    
    fetchUserAssignedDates();
  }, [userData]);

  // Initialize form with existing data when editing
  useEffect(() => {
    if (tourData) {
      // Set common name field based on type
      if (tourData.type === 'show') {
        setEventName(tourData.showName || tourData.tourName || '');
      } else {
        setEventName(tourData.tourName || '');
      }
      
      setActName(tourData.actName);
      setActType(tourData.actType);
      setCoverPhotoUrl(tourData.tourCoverPhotoUrl);
      setEventType(tourData.type || 'tour'); // Set event type based on existing data
      
      // Load selected dates
      const dates = tourData.eventDates.map(td => new Date(td.date));
      setSelectedDates(dates);
  
      // Initialize venue details - load from eventDates subcollection
      const fetchVenueDetails = async () => {
        try {
          const db = getFirestoreInstance();
          const collectionName = tourData.type === 'tour' ? 'tours' : 'shows';
          const venueDetailsObj = {};
      
          // Fetch all dates from the eventDates subcollection
          const datesSnapshot = await getDocs(
            collection(db, collectionName, tourData.id, 'eventDates')
          );
      
          datesSnapshot.forEach(doc => {
            const dateData = doc.data();
            if (!dateData.date) return;
      
            // Create a Date object to get timestamp
            const dateObj = new Date(dateData.date);
            const dateKey = dateObj.getTime().toString();
            
            // Get venue data from the date document
            if (dateData.event?.venueDetails?.venueData) {
              // Store venue data and add the top-level venueLocked field
              venueDetailsObj[dateKey] = {
                ...dateData.event.venueDetails.venueData,
                venueLocked: dateData.venueLocked || dateData.venueVerified || false
              };
            }
          });
      
          setVenueDetails(venueDetailsObj);
        } catch (error) {
          console.error('Error fetching venue details from subcollection:', error);
        }
      };
  
      // Execute the venue details fetch
      fetchVenueDetails();
  
      // Fetch and process assignedUsers and pendingInvitations from subcollections
      const fetchUsersData = async () => {
        try {
          const db = getFirestoreInstance();
          const collectionName = tourData.type === 'tour' ? 'tours' : 'shows';
          
          // First fetch assignedUsers subcollection (users who accepted invitations)
          const assignedUsersQuery = collection(db, collectionName, tourData.id, 'assignedUsers');
          const assignedUsersSnapshot = await getDocs(assignedUsersQuery);
          
          // Process each user type (artist, crew, manager, etc.)
          const artists = [];
          const crew = [];
          const managers = [];
          const organizations = [];
          
          // Track date assignments for each user type
          const artistAssignments = {};
          const crewAssignments = {};
          const managerAssignments = {};
          const organizationAssignments = {};
          
          // Track permissions for each user type
          const artistPerms = {};
          const crewPerms = {};
          const managerPerms = {};
          const organizationPerms = {};
          
          // Track confirmed dates by user
          const confirmedDates = {};
          
          assignedUsersSnapshot.forEach(doc => {
            const userData = doc.data();
            if (!userData.userId) return; // Skip if missing userId
            
            // Convert assignedDates to ISO strings for state compatibility
            const assignedDatesIso = (userData.assignedDates || [])
              .filter(date => date.status === 'accepted')
              .map(date => {
                // If date has a native date field, use it; otherwise use dateDocId to find in eventDates
                const dateStr = date.date || 
                  tourData.eventDates.find(td => td.dateDocId === date.dateDocId)?.date;
                
                if (dateStr) {
                  const dateObj = new Date(dateStr);
                  return dateObj.toISOString();
                }
                return null;
              })
              .filter(Boolean);
            
            // Store confirmed dates for later reference
            confirmedDates[userData.userId] = assignedDatesIso;
            
            // Add user to appropriate array based on role
            const userDetails = {
              userId: userData.userId,
              fullName: userData.userDetails?.fullName || userData.userName || '',
              userName: userData.userDetails?.userName || '',
              email: userData.userDetails?.email || '',
              profilePictureUrl: userData.userDetails?.profilePictureUrl || '',
              userType: userData.userDetails?.userType || userData.role,
              userSubType: userData.userDetails?.userSubType || '',
              idVerificationStatus: userData.userDetails?.idVerificationStatus || 'Unverified',
            };
            
            // Store permissions for this user
            const permissions = userData.permissions || {};
            
            switch (userData.role?.toLowerCase()) {
              case 'artist':
                artists.push(userDetails);
                artistAssignments[userData.userId] = assignedDatesIso;
                artistPerms[userData.userId] = permissions;
                break;
              case 'crew':
                crew.push(userDetails);
                crewAssignments[userData.userId] = assignedDatesIso;
                crewPerms[userData.userId] = permissions;
                break;
              case 'manager':
                managers.push(userDetails);
                managerAssignments[userData.userId] = assignedDatesIso;
                managerPerms[userData.userId] = permissions;
                break;
              case 'organization':
                organizations.push(userDetails);
                organizationAssignments[userData.userId] = assignedDatesIso;
                organizationPerms[userData.userId] = permissions;
                break;
            }
          });
          
          // Now fetch pendingInvitations subcollection
          const pendingInvitationsQuery = collection(db, collectionName, tourData.id, 'pendingInvitations');
          const pendingInvitationsSnapshot = await getDocs(pendingInvitationsQuery);
          
          // Track pending dates by user
          const pendingDates = {};
          const pendingInvs = [];
          
          pendingInvitationsSnapshot.forEach(doc => {
            const invitation = doc.data();
            
            // Only include pending invitations
            if (invitation.status !== 'pending') return;
            
            pendingInvs.push({
              id: doc.id,
              ...invitation
            });
            
            // Convert assignedDates to ISO strings for state compatibility
            const pendingDatesIso = (invitation.assignedDates || [])
              .map(date => {
                // If date has a native date field, use it; otherwise use dateDocId to find in eventDates
                const dateStr = date.date || 
                  tourData.eventDates.find(td => td.dateDocId === date.dateDocId)?.date;
                
                if (dateStr) {
                  const dateObj = new Date(dateStr);
                  return dateObj.toISOString();
                }
                return null;
              })
              .filter(Boolean);
            
            // Store pending dates for this user
            if (invitation.userId) {
              pendingDates[invitation.userId] = pendingDatesIso;
              
              // If user not already in the appropriate array, add them
              const userDetails = {
                userId: invitation.userId,
                fullName: invitation.fullName || invitation.userName || '',
                userName: invitation.userName || '',
                email: invitation.email || '',
                profilePictureUrl: invitation.profilePictureUrl || '',
                userType: invitation.userType || invitation.role,
                userSubType: invitation.userSubType || '',
                idVerificationStatus: invitation.idVerificationStatus || 'Unverified',
              };
              
              // Store permissions for this user
              const permissions = invitation.permissions || {};
              
              // Add to appropriate array and assignments based on role
              switch (invitation.role?.toLowerCase()) {
                case 'artist':
                  if (!artists.some(a => a.userId === invitation.userId)) {
                    artists.push(userDetails);
                  }
                  artistAssignments[invitation.userId] = [
                    ...(artistAssignments[invitation.userId] || []),
                    ...pendingDatesIso
                  ];
                  artistPerms[invitation.userId] = permissions;
                  break;
                case 'crew':
                  if (!crew.some(c => c.userId === invitation.userId)) {
                    crew.push(userDetails);
                  }
                  crewAssignments[invitation.userId] = [
                    ...(crewAssignments[invitation.userId] || []),
                    ...pendingDatesIso
                  ];
                  crewPerms[invitation.userId] = permissions;
                  break;
                case 'manager':
                  if (!managers.some(m => m.userId === invitation.userId)) {
                    managers.push(userDetails);
                  }
                  managerAssignments[invitation.userId] = [
                    ...(managerAssignments[invitation.userId] || []),
                    ...pendingDatesIso
                  ];
                  managerPerms[invitation.userId] = permissions;
                  break;
                case 'organization':
                  if (!organizations.some(o => o.userId === invitation.userId)) {
                    organizations.push(userDetails);
                  }
                  organizationAssignments[invitation.userId] = [
                    ...(organizationAssignments[invitation.userId] || []),
                    ...pendingDatesIso
                  ];
                  organizationPerms[invitation.userId] = permissions;
                  break;
              }
            }
          });
          
          // Store the fetched data in component state
          setSelectedArtists(artists);
          setSelectedCrew(crew);
          setSelectedManagers(managers);
          setSelectedOrganizations(organizations);
          
          setArtistDateAssignments(artistAssignments);
          setCrewDateAssignments(crewAssignments);
          setManagerDateAssignments(managerAssignments);
          setOrganizationDateAssignments(organizationAssignments);
          
          setArtistPermissions(artistPerms);
          setCrewPermissions(crewPerms);
          setManagerPermissions(managerPerms);
          setOrganizationPermissions(organizationPerms);
          
          setConfirmedDatesByUser(confirmedDates);
          setPendingDatesByUser(pendingDates);
          setPendingInvitations(pendingInvs);
          
          // Now also fetch bands - similar process
          const fetchBandData = async () => {
            try {
              // Check for bands in assignedUsers with 'band_' prefix
              const bandUsers = assignedUsersSnapshot.docs
                .filter(doc => doc.id.startsWith('band_'))
                .map(doc => doc.data());
              
              const bands = [];
              const bandAssignmentsObj = {};
              const bandPermissionsObj = {};
              
              bandUsers.forEach(bandData => {
                if (!bandData.id) return;
                
                const bandId = bandData.id;
                
                // Convert assignedDates to ISO strings
                const assignedDatesIso = (bandData.assignedDates || [])
                  .filter(date => date.status === 'accepted')
                  .map(date => {
                    const dateStr = date.date || 
                      tourData.eventDates.find(td => td.dateDocId === date.dateDocId)?.date;
                    
                    if (dateStr) {
                      const dateObj = new Date(dateStr);
                      return dateObj.toISOString();
                    }
                    return null;
                  })
                  .filter(Boolean);
                
                bands.push({
                  id: bandId,
                  bandFullName: bandData.bandDetails?.bandFullName || '',
                  bandUserName: bandData.bandDetails?.bandUserName || '',
                  bandProfilePictureUrl: bandData.bandDetails?.bandProfilePictureUrl || '',
                  bandGenres: bandData.bandDetails?.bandGenres || []
                });
                
                bandAssignmentsObj[bandId] = assignedDatesIso;
                bandPermissionsObj[bandId] = bandData.permissions || roleDefaultPermissions.band;
              });
              
              // Now look for bands in pendingInvitations
              const bandInvitations = pendingInvs.filter(inv => inv.bandId);
              
              bandInvitations.forEach(invitation => {
                const bandId = invitation.bandId;
                
                // Convert assignedDates to ISO strings
                const pendingDatesIso = (invitation.assignedDates || [])
                  .map(date => {
                    const dateStr = date.date || 
                      tourData.eventDates.find(td => td.dateDocId === date.dateDocId)?.date;
                    
                    if (dateStr) {
                      const dateObj = new Date(dateStr);
                      return dateObj.toISOString();
                    }
                    return null;
                  })
                  .filter(Boolean);
                
                // If band not already in the bands array, add it
                if (!bands.some(b => b.id === bandId)) {
                  bands.push({
                    id: bandId,
                    bandFullName: invitation.bandName || '',
                    bandUserName: invitation.bandUserName || '',
                    bandProfilePictureUrl: invitation.bandProfilePictureUrl || '',
                    bandGenres: invitation.bandGenres || []
                  });
                }
                
                // Add pending dates to the assignments
                bandAssignmentsObj[bandId] = [
                  ...(bandAssignmentsObj[bandId] || []),
                  ...pendingDatesIso
                ];
                
                // Set permissions
                bandPermissionsObj[bandId] = invitation.permissions || roleDefaultPermissions.band;
              });
              
              // Update state with band data
              setSelectedBands(bands);
              setBandDateAssignments(bandAssignmentsObj);
              setBandPermissions(bandPermissionsObj);
              
            } catch (error) {
              console.error('Error fetching band data:', error);
            }
          };
          
          // Execute band data fetch
          await fetchBandData();
          
        } catch (error) {
          console.error('Error fetching user data from subcollections:', error);
        }
      };
      
      // Execute the data fetch
      fetchUsersData();
    }
  }, [tourData]);

  // Check artists, bands, crew availability for assigned dates
  useEffect(() => {
    const checkUsersAvailability = async () => {
      // Check availability for artists
      const checkAvailability = async (users, setAvailabilityState) => {
        const availabilityMap = {};
        const db = getFirestoreInstance();
        
        for (const user of users) {
          const userId = user.userId || user.id;
          if (!userId) continue;
          
          try {
            // Fetch user's assigned dates
            const userAssignedDatesRef = collection(db, 'users', userId, 'assignedEventDates');
            const snapshot = await getDocs(userAssignedDatesRef);
            
            const dateAvailability = {};
            
            snapshot.forEach(doc => {
              const data = doc.data();
              const dates = data.dates || [];
              
              dates.forEach(dateInfo => {
                const dateStr = dateInfo.date;
                if (dateStr) {
                  // Convert to ISO string for comparison with selectedDates
                  const dateObj = new Date(dateStr);
                  const dateIso = dateObj.toISOString();
                  
                  // Store the conflict information
                  dateAvailability[dateIso] = {
                    available: false,
                    conflictDetails: {
                      eventName: dateInfo.eventName || 'Unknown Event',
                      venueDisplayName: dateInfo.venue?.displayName || 'Unknown Venue',
                      venueAddress: dateInfo.venue?.formattedAddress || 'Unknown Location'
                    }
                  };
                }
              });
            });
            
            availabilityMap[userId] = dateAvailability;
          } catch (error) {
            console.error(`Error checking availability for user ${userId}:`, error);
          }
        }
        
        setAvailabilityState(availabilityMap);
      };
      
      // Check availability for all user types
      await Promise.all([
        checkAvailability(selectedArtists, setArtistDateAvailability),
        checkAvailability(selectedCrew, setCrewDateAvailability),
        checkAvailability(selectedBands, setBandDateAvailability),
        checkAvailability(selectedManagers, setManagerDateAvailability),
        checkAvailability(selectedOrganizations, setOrganizationDateAvailability)
      ]);
    };
    
    if (selectedDates.length > 0) {
      checkUsersAvailability();
    }
  }, [selectedArtists, selectedCrew, selectedBands, selectedManagers, selectedOrganizations, selectedDates]);

  const getPageTitle = () => {
    if (isEditing) return 'Edit Tour';
    return eventType === 'tour' ? 'Create Tour' : 'Add Show';
  };

  const getSubmitButtonText = () => {
    if (loading) return loadingMessage || 'Processing...';
    if (isEditing) return 'Update Tour';
    return eventType === 'tour' ? 'Create Tour' : 'Add Show';
  };

  // Check tour creation limits based on membership tier
  const checkEventLimit = async () => {
    if (!userData?.userId || !userData?.membershipTier) return false;
    
    const db = getFirestoreInstance();
    
    try {
      // Query the user's assignedEvents subcollection
      const eventsQuery = eventType === 'tour' 
        ? query(collection(db, 'users', userData.userId, 'assignedEvents'), where('type', '==', 'tour'))
        : query(collection(db, 'users', userData.userId, 'assignedEvents'), where('type', '==', 'show'));
      
      const eventsSnapshot = await getDocs(eventsQuery);
      
      // Count only events where the user is the creator
      const createdEventsCount = eventsSnapshot.docs.filter(doc => doc.data().isCreator === true).length;
      
      // Apply limits based on membership tier and event type
      switch (userData.membershipTier) {
        case 'Free':
          return eventType === 'tour' 
            ? createdEventsCount < 1   // 1 tour limit
            : createdEventsCount < 5;  // 5 shows limit
        case 'Basic':
          return eventType === 'tour' 
            ? createdEventsCount < 10  // 10 tours limit
            : createdEventsCount < 20; // 20 shows limit
        case 'Pro':
          return eventType === 'tour' 
            ? createdEventsCount < 15  // 15 tours limit
            : createdEventsCount < 40; // 40 shows limit
        case 'Elite':
          return true; // Unlimited for both tours and shows
        default:
          return false;
      }
    } catch (error) {
      console.error('Error checking event limits:', error);
      // Default to not allowing creation on error
      return false;
    }
  };

  // Function to fetch assigned dates for a specific month
const fetchUserAssignedDatesForMonth = async (year, month) => {
  if (!userData || !userData.userId) return;
  
  try {
    const db = getFirestoreInstance();
    // Format month_year as YYYY-MM
    const monthYear = `${year}-${String(month + 1).padStart(2, '0')}`;
    
    // Get the specific document for this month
    const assignedDateRef = doc(db, 'users', userData.userId, 'assignedEventDates', monthYear);
    const docSnapshot = await getDoc(assignedDateRef);
    
    const assignedDatesMap = { ...userAssignedDates }; // Keep existing dates
    
    if (docSnapshot.exists()) {
      const dateData = docSnapshot.data();
      const dates = dateData.dates || [];
      
      dates.forEach(dateInfo => {
        const date = dateInfo.date;
        assignedDatesMap[date] = {
          ...dateInfo,
          docId: monthYear
        };
      });
    }
    
    setUserAssignedDates(assignedDatesMap);
  } catch (error) {
    console.error(`Error fetching assigned dates for ${year}-${month}:`, error);
  }
};

// Call this when activeMonth changes
useEffect(() => {
  fetchUserAssignedDatesForMonth(
    activeMonth.getFullYear(),
    activeMonth.getMonth()
  );
}, [activeMonth, userData]);

  // File handling functions
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCoverPhoto(file);
      const previewUrl = URL.createObjectURL(file);
      setCoverPhotoPreview(previewUrl);
    }
  };

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (coverPhotoPreview) {
        URL.revokeObjectURL(coverPhotoPreview);
      }
    };
  }, [coverPhotoPreview]);

  // Update useEffect for handling date changes
 useEffect(() => {
  // Initialize venue results and details for new dates
  setVenueResults(prev => {
    const newResults = {};
    selectedDates.forEach(date => {
      const dateKey = date.getTime().toString();
      newResults[dateKey] = prev[dateKey] || [];
    });
    return newResults;
  });

  // Keep venue details only for selected dates - don't overwrite with empty objects
  setVenueDetails(prev => {
    const newVenueDetails = {};
    selectedDates.forEach(date => {
      const dateKey = date.getTime().toString();
      if (prev[dateKey]) {
        // Keep existing venue details
        newVenueDetails[dateKey] = prev[dateKey];
      }
    });
    return newVenueDetails;
  });
}, [selectedDates]);

  // Helper function to check if a date is available
  const isDateAvailable = (date) => {
    if (!date || !userData) return true;
    
    // Format the date to match our stored format
    const dateStr = formatLocalDate(date);
    
    // Check if this date is in userAssignedDates
    const assignedDate = userAssignedDates[dateStr];
    
    // If no assigned date, or if it belongs to the current event being edited, consider it available
    if (!assignedDate || (isEditing && assignedDate.eventId === tourData?.id)) {
      return true;
    }
    
    // Otherwise, it's booked with another event
    return false;
  };

  // Format a date as YYYY-MM-DD
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Show date conflict modal
  const showConflictModal = (date) => {
    const dateStr = formatLocalDate(date);
    const conflict = userAssignedDates[dateStr];
    
    // Don't show conflict modal if the date belongs to the current event being edited
    if (conflict && (!isEditing || conflict.eventId !== tourData?.id)) {
      setConflictDetails({
        date: date,
        eventName: conflict.eventName || 'Unknown Event',
        venueDisplayName: conflict.venue?.displayName || 'Unknown Venue',
        venueAddress: conflict.venue?.formattedAddress || 'Unknown Location'
      });
      setShowDateConflictModal(true);
    }
  };

const isVenueLocked = (date) => {
  if (!date) return false;
  
  // First check if the date exists in the selected dates
  const dateIndex = selectedDates.findIndex(d => 
    d.getFullYear() === date.getFullYear() &&
    d.getMonth() === date.getMonth() &&
    d.getDate() === date.getDate()
  );
  
  if (dateIndex === -1) return false;
  
  // Use the matching date's timestamp
  const matchedDate = selectedDates[dateIndex];
  const dateKey = matchedDate.getTime().toString();
  
  // Check for the top-level venueLocked field
  return Boolean(venueDetails[dateKey]?.venueLocked);
};

  // Add this component for venue warning modal
  const VenueWarningModal = ({ show, onConfirm, onCancel }) => {
    if (!show) return null;

    return (
      <div className={styles.modalOverlay} onClick={onCancel}>
        <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
          <h3 className={styles.modalTitle}>Missing Venues</h3>
          <p className={styles.modalText}>
            You have not added venues for all tour dates. You can add them later if you wish.
          </p>
          <p className={styles.modalSubText}>
            Would you like to continue?
          </p>
          <div className={styles.modalActions}>
            <button className={styles.modalCancelButton} onClick={onCancel}>
              Go Back
            </button>
            <button className={styles.modalConfirmButton} onClick={onConfirm}>
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Date conflict modal component
  const DateConflictModal = ({ show, details, onClose }) => {
    if (!show || !details) return null;
    
    return (
      <div className={styles.modalOverlay} onClick={onClose}>
        <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
          <h3 className={styles.modalTitle}>
            <FontAwesomeIcon icon={faExclamationTriangle} className={styles.warningIcon} />
            Date Conflict
          </h3>
          <p className={styles.modalText}>
            You already have an event scheduled on {details.date.toLocaleDateString()}.
          </p>
          <div className={styles.conflictDetails}>
            <p><strong>Event:</strong> {details.eventName}</p>
            <p><strong>Venue:</strong> {details.venueDisplayName}</p>
            <p><strong>Location:</strong> {details.venueAddress}</p>
          </div>
          <p className={styles.modalSubText}>
            Please select a different date.
          </p>
          <div className={styles.modalActions}>
            <button className={styles.modalConfirmButton} onClick={onClose}>
              Okay
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Check if all dates have venues
  const allDatesHaveVenues = () => {
    return selectedDates.every(date => 
      venueDetails[date.getTime().toString()]
    );
  };

  // Update the handleVenueSearch function
  const handleVenueSearch = async () => {
    if (!venueSearchQuery.trim()) return;
    
    const currentDate = selectedDates[selectedDateIndex];
    if (!currentDate) return;
    
    // Use timestamp as unique key
    const dateKey = currentDate.getTime().toString();
    
    try {
      setSearchingVenue(true);
      setIsSearchButtonPressed(true); // Set this when search begins
  
      const { results } = await client.search([{
        indexName: 'places_index',
        query: venueSearchQuery,
        hitsPerPage: 5
      }]);
  
      setVenueResults(prev => ({
        ...prev,
        [dateKey]: results[0].hits || []
      }));
    } catch (error) {
      console.error('Error searching venues:', error);
      setVenueResults(prev => ({
        ...prev,
        [dateKey]: []
      }));
    } finally {
      setSearchingVenue(false);
    }
  };
  
  // Update handleVenueSelect to use timestamp
  const handleVenueSelect = (venue, date) => {
    if (!date) return;
    
    const dateKey = date.getTime().toString();
    
    // Check if the venue is locked - use the existing venueLocked value if available
    const currentVenueLocked = venueDetails[dateKey]?.venueLocked || false;
    
    if (currentVenueLocked) {
      alert("This venue is locked. One or more users have verified this show and the venue cannot be changed.");
      return;
    }
  
    // When setting venue details, preserve the top-level venueLocked flag
    setVenueDetails(prev => ({
      ...prev,
      [dateKey]: {
        ...venue,
        id: venue.id || venue.placeId,
        displayName: venue.displayName || venue.name,
        formattedAddress: venue.formattedAddress || venue.address,
        dateTimestamp: dateKey,
        venueLocked: currentVenueLocked // Keep the existing lock status
      }
    }));
  
    setVenueResults(prev => ({
      ...prev,
      [dateKey]: []
    }));
    
    setVenueSearchQuery(''); // Clear search query
    setIsSearchButtonPressed(false); // Reset search state
  };
  

  const handleVenueSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleVenueSearch();
    }
  };

  // Function to search for bands
  const handleBandSearch = async () => {
    if (!bandSearchQuery.trim()) return;
    
    try {
      setSearchingBands(true);
      const { results } = await client.search([{
        indexName: 'bands_index',
        query: bandSearchQuery,
        hitsPerPage: 10
      }]);

      const formattedBands = results[0].hits.map(hit => ({
        id: hit.objectID,
        bandFullName: hit.bandFullName,
        bandUserName: hit.bandUserName,
        bandProfilePictureUrl: hit.bandProfilePictureUrl,
        bandGenres: hit.bandGenres || [],
        isAssigned: selectedBands.some(band => band.id === hit.objectID)
      }));
      
      setBandResults(formattedBands);
    } catch (error) {
      console.error('Error searching bands:', error);
      setBandResults([]);
    } finally {
      setSearchingBands(false);
    }
  };

  // Function to handle adding a band
  const handleAddBand = (band) => {
    if (!selectedBands.some(b => b.id === band.id)) {
      setSelectedBands(prevSelectedBands => [...prevSelectedBands, {
        id: band.id,
        bandFullName: band.bandFullName,
        bandUserName: band.bandUserName,
        bandProfilePictureUrl: band.bandProfilePictureUrl,
        bandGenres: band.bandGenres
      }]);
      
      // Initialize empty date assignments for this band
      setBandDateAssignments(prevAssignments => ({
        ...prevAssignments,
        [band.id]: []
      }));
      
      // Initialize default permissions for this band
      setBandPermissions(prevPermissions => ({
        ...prevPermissions,
        [band.id]: { ...roleDefaultPermissions.band }
      }));

      // Check band availability for dates
      checkBandAvailability(band.id);
    }
    setBandSearchQuery('');
    setBandResults([]);
  };

  // Function to check band availability
  const checkBandAvailability = async (bandId) => {
    try {
      const db = getFirestoreInstance();
      const bandAvailability = {};
      
      // Fetch band's assigned dates from bands collection
      const bandEventsQuery = collection(db, 'band_events', bandId, 'assignedEvents');
      const bandEventsSnapshot = await getDocs(bandEventsQuery);
      
      for (const eventDoc of bandEventsSnapshot.docs) {
        const eventData = eventDoc.data();
        const eventDatesRef = collection(db, 'band_events', bandId, 'assignedEvents', eventDoc.id, 'eventDates');
        const eventDatesSnapshot = await getDocs(eventDatesRef);
        
        eventDatesSnapshot.docs.forEach(dateDoc => {
          const dateData = dateDoc.data();
          if (dateData.date) {
            const dateObj = new Date(dateData.date);
            const dateIso = dateObj.toISOString();
            
            bandAvailability[dateIso] = {
              available: false,
              conflictDetails: {
                eventName: eventData.tourName || eventData.showName || 'Unknown Event',
                venueDisplayName: dateData.venueDetails?.displayName || 'Unknown Venue',
                venueAddress: dateData.venueDetails?.formattedAddress || 'Unknown Location'
              }
            };
          }
        });
      }
      
      // Update state with band availability
      setBandDateAvailability(prev => ({
        ...prev,
        [bandId]: bandAvailability
      }));
    } catch (error) {
      console.error(`Error checking band availability for ${bandId}:`, error);
    }
  };

  // Function to handle removing a band
  const handleRemoveBand = (bandId) => {
    setSelectedBands(prev => prev.filter(band => band.id !== bandId));
    setBandDateAssignments(prev => {
      const newAssignments = { ...prev };
      delete newAssignments[bandId];
      return newAssignments;
    });
    setBandPermissions(prev => {
      const newPermissions = { ...prev };
      delete newPermissions[bandId];
      return newPermissions;
    });
    // Also remove band availability data
    setBandDateAvailability(prev => {
      const newAvailability = { ...prev };
      delete newAvailability[bandId];
      return newAvailability;
    });
  };

  // Function to handle date selection for a band
  const handleBandDateSelection = (bandId, date) => {
    const dateString = date.toISOString();
    
    // Check if the date is available for this band
    const bandAvailability = bandDateAvailability[bandId] || {};
    if (bandAvailability[dateString] && !bandAvailability[dateString].available) {
      // Show conflict details
      const conflict = bandAvailability[dateString].conflictDetails;
      setConflictDetails({
        date: date,
        eventName: conflict.eventName,
        venueDisplayName: conflict.venueDisplayName,
        venueAddress: conflict.venueAddress
      });
      setShowDateConflictModal(true);
      return;
    }
    
    setBandDateAssignments(prev => {
      const currentDates = prev[bandId] || [];
      return {
        ...prev,
        [bandId]: currentDates.includes(dateString)
          ? currentDates.filter(d => d !== dateString)
          : [...currentDates, dateString]
      };
    });
  };

  // Function to select all dates for a band
  const handleSelectAllDatesForBand = (bandId) => {
    const allDates = selectedDates.map(date => date.toISOString());
    
    // Filter only available dates
    const bandAvailability = bandDateAvailability[bandId] || {};
    const availableDates = allDates.filter(dateString => {
      return !bandAvailability[dateString] || bandAvailability[dateString].available;
    });
    
    setBandDateAssignments(prev => {
      const currentDates = prev[bandId] || [];
      const shouldSelectAll = currentDates.length !== availableDates.length;
      
      if (shouldSelectAll) {
        return { ...prev, [bandId]: availableDates };
      } else {
        return { ...prev, [bandId]: [] };
      }
    });
  };

  // Search handlers for artists and crew
  const handleArtistSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleArtistSearch();
    }
  };

  const handleCrewSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleCrewSearch();
    }
  };

  // Update the venue details display in step 3
  const renderVenueDetails = () => {
    const currentDate = selectedDates[selectedDateIndex];
    if (!currentDate) return null;
  
    const dateKey = currentDate.getTime().toString();
    const venueForDate = venueDetails[dateKey];
  
    if (venueForDate) {
      return (
        <div className={styles.selectedVenue}>
          <div className={styles.selectedVenueContainer}>
            <img 
              src={venueForDate.placeCoverPhotoUrl || defaultVenueImage}
              alt={venueForDate.displayName}
              className={styles.selectedVenueImage}
            />
            <div className={styles.venueInfo}>
              <h4 className={styles.venueName}>{venueForDate.displayName}</h4>
              <p className={styles.venueAddress}>{venueForDate.formattedAddress}</p>
              
              {venueForDate.venueLocked && (
                <div className={styles.venueLocked}>
                  <FontAwesomeIcon icon={faLock} />
                  <span>Venue is locked. This show has been verified by one or more users.</span>
                </div>
              )}
            </div>
          </div>
          
          <div className={styles.mapContainer}>
            <iframe
              title="Location Map"
              width="100%"
              height="100%"
              frameBorder="0"
              style={{ border: 0 }}
              referrerPolicy="no-referrer-when-downgrade"
              src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyDX3zmGo2kfdaD1jmTOzjoCP4D2rfZ3c3A&q=${encodeURIComponent(venueForDate.formattedAddress)}&zoom=20&maptype=satellite&language=en`}
              allowFullScreen
            />
          </div>
        </div>
      );
    }
  
    return null;
  };

  useEffect(() => {
    if (tourData) {
      console.log('Tour data loaded:', tourData.id);
      console.log('Number of dates:', tourData.eventDates?.length || 0);
      
      // Log any venue details in the main tour dates
      if (tourData.eventDates) {
        tourData.eventDates.forEach(date => {
          if (date.event?.venueDetails?.venueData) {
            console.log(`Date ${date.date} has venue:`, 
              date.event.venueDetails.venueData.displayName || 'Unnamed venue');
          } else {
            console.log(`Date ${date.date} has no venue data`);
          }
        });
      }
    }
  }, [tourData]);
  
  // Update venue display in date selector
  const renderDateSelector = () => {
    return (
      <div className={styles.dateSelectorContainer}>
        <div className={styles.datesScrollContainer}>
          {selectedDates.map((date, index) => {
            const dateKey = date.getTime().toString();
            const hasVenue = Boolean(venueDetails[dateKey]);
            
            return (
              <div 
                key={dateKey}
                className={`${styles.dateBox} ${selectedDateIndex === index ? styles.active : ''}`}
                onClick={() => setSelectedDateIndex(index)}
              >
                <div className={styles.dateBoxDay}>
                  {date.toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric'
                  })}
                </div>
                <div className={styles.dateBoxVenue}>
                  {hasVenue ? venueDetails[dateKey].displayName : 'No venue selected'}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };
  
  // Function to render venue search results
  const renderVenueResults = () => {
    const currentDate = selectedDates[selectedDateIndex];
    if (!currentDate) return null;
    
    const dateKey = currentDate.getTime().toString();
    const currentResults = venueResults[dateKey] || [];
  
    if (searchingVenue) {
      return (
        <div className={styles.searchingMessage}>
          Searching...
        </div>
      );
    }
  
    if (currentResults.length > 0) {
      return (
        <div className={styles.venueResults}>
          {currentResults.map((venue, index) => (
            <div
              key={`venue-result-${venue.id}-${index}`}
              className={styles.venueItem}
              onClick={() => handleVenueSelect(venue, currentDate)}
            >
              <img 
                src={venue.placeCoverPhotoUrl || defaultVenueImage}
                alt={venue.displayName}
                className={styles.venueResultImage}
              />
              <div className={styles.venueInfoContainer}>
                <h4 className={styles.venueName}>{venue.displayName}</h4>
                <p className={styles.venueAddress}>{venue.formattedAddress}</p>
              </div>
            </div>
          ))}
        </div>
      );
    }
  
    // Only show no results message after search
    if (venueSearchQuery && isSearchButtonPressed) {
      return (
        <div className={styles.noVenueResults}>
          <p className={styles.noResultsText}>Couldn't find venue in our database</p>
          <button 
            className={styles.searchMapButton}
            onClick={() => setShowMapSearch(true)}
          >
            Search on Map
          </button>
        </div>
      );
    }
  
    return null;
  };

  // Helper function to check if a user is already invited
  const isUserInvited = (userId) => {
    // Check if the user is in selectedArtists or selectedCrew
    const isSelected = 
      selectedArtists.some(artist => artist.userId === userId) ||
      selectedCrew.some(crew => crew.userId === userId) ||
      selectedManagers.some(manager => manager.userId === userId) ||
      selectedOrganizations.some(org => org.userId === userId);
    
    // Check if the user has a pending invitation
    const hasPendingInvitation = pendingInvitations.some(inv => inv.userId === userId);
    
    return isSelected || hasPendingInvitation;
  };

  // Artist handling functions
  const handleArtistSearch = async () => {
    if (!artistSearchQuery.trim()) return;
    
    try {
      setSearchingArtists(true);
      const { results } = await client.search([{
        indexName: 'users_index',
        query: artistSearchQuery,
        filters: 'userType:Artist',
        hitsPerPage: 10
      }]);
  
      const formattedArtists = results[0].hits.map(hit => ({
        id: hit.objectID,
        fullName: hit.fullName,
        email: hit.email,
        userName: hit.userName,
        userSubType: hit.userSubType,
        profilePictureUrl: hit.profilePictureUrl,
        userType: hit.userType,
        idVerificationStatus: hit.idVerificationStatus,
        isPending: pendingInvitations.some(inv => inv.userId === hit.objectID && inv.role === 'Artist'),
        isAssigned: selectedArtists.some(artist => artist.userId === hit.objectID)
      }));
      
      setArtistResults(formattedArtists);
    } catch (error) {
      console.error('Error searching artists:', error);
      setArtistResults([]);
    } finally {
      setSearchingArtists(false);
    }
  };

  // Update handleCrewSearch function
  const handleCrewSearch = async () => {
    if (!crewSearchQuery.trim()) return;
    
    try {
      setSearchingCrew(true);
      const { results } = await client.search([{
        indexName: 'users_index',
        query: crewSearchQuery,
        filters: 'userType:Crew',
        hitsPerPage: 10
      }]);
  
      const formattedCrew = results[0].hits.map(hit => ({
        id: hit.objectID,
        fullName: hit.fullName,
        email: hit.email,
        userName: hit.userName,
        userSubType: hit.userSubType,
        profilePictureUrl: hit.profilePictureUrl,
        userType: hit.userType,
        idVerificationStatus: hit.idVerificationStatus,
        isPending: pendingInvitations.some(inv => inv.userId === hit.objectID && inv.role === 'Crew'),
        isAssigned: selectedCrew.some(crew => crew.userId === hit.objectID)
      }));
      
      setCrewResults(formattedCrew);
    } catch (error) {
      console.error('Error searching crew:', error);
      setCrewResults([]);
    } finally {
      setSearchingCrew(false);
    }
  };

  // New function to search for managers
  const handleManagerSearch = async () => {
    if (!managerSearchQuery.trim()) return;
    
    try {
      setSearchingManagers(true);
      const { results } = await client.search([{
        indexName: 'users_index',
        query: managerSearchQuery,
        filters: 'userType:Manager',
        hitsPerPage: 10
      }]);
  
      const formattedManagers = results[0].hits.map(hit => ({
        id: hit.objectID,
        fullName: hit.fullName,
        email: hit.email,
        userName: hit.userName,
        userSubType: hit.userSubType,
        profilePictureUrl: hit.profilePictureUrl,
        userType: hit.userType,
        idVerificationStatus: hit.idVerificationStatus,
        isPending: pendingInvitations.some(inv => inv.userId === hit.objectID && inv.role === 'Manager'),
        isAssigned: selectedManagers.some(manager => manager.userId === hit.objectID)
      }));
      
      setManagerResults(formattedManagers);
    } catch (error) {
      console.error('Error searching managers:', error);
      setManagerResults([]);
    } finally {
      setSearchingManagers(false);
    }
  };

  // New function to search for organizations
  const handleOrganizationSearch = async () => {
    if (!organizationSearchQuery.trim()) return;
    
    try {
      setSearchingOrganizations(true);
      const { results } = await client.search([{
        indexName: 'users_index',
        query: organizationSearchQuery,
        filters: 'userType:Organization',
        hitsPerPage: 10
      }]);
  
      const formattedOrganizations = results[0].hits.map(hit => ({
        id: hit.objectID,
        fullName: hit.fullName,
        email: hit.email,
        userName: hit.userName,
        userSubType: hit.userSubType,
        profilePictureUrl: hit.profilePictureUrl,
        userType: hit.userType,
        idVerificationStatus: hit.idVerificationStatus,
        isPending: pendingInvitations.some(inv => inv.userId === hit.objectID && inv.role === 'Organization'),
        isAssigned: selectedOrganizations.some(org => org.userId === hit.objectID)
      }));
      
      setOrganizationResults(formattedOrganizations);
    } catch (error) {
      console.error('Error searching organizations:', error);
      setOrganizationResults([]);
    } finally {
      setSearchingOrganizations(false);
    }
  };

  // Function to check artist availability
  const checkUserAvailability = async (userId, userType) => {
    try {
      const db = getFirestoreInstance();
      const dateAvailability = {};
      
      // Fetch user's assigned dates
      const userAssignedDatesRef = collection(db, 'users', userId, 'assignedEventDates');
      const snapshot = await getDocs(userAssignedDatesRef);
      
      snapshot.forEach(doc => {
        const data = doc.data();
        const dates = data.dates || [];
        
        dates.forEach(dateInfo => {
          const dateStr = dateInfo.date;
          if (dateStr) {
            const dateObj = new Date(dateStr);
            const dateIso = dateObj.toISOString();
            
            dateAvailability[dateIso] = {
              available: false,
              conflictDetails: {
                eventName: dateInfo.eventName || 'Unknown Event',
                venueDisplayName: dateInfo.venue?.displayName || 'Unknown Venue',
                venueAddress: dateInfo.venue?.formattedAddress || 'Unknown Location'
              }
            };
          }
        });
      });
      
      // Update availability state based on user type
      switch (userType.toLowerCase()) {
        case 'artist':
          setArtistDateAvailability(prev => ({
            ...prev,
            [userId]: dateAvailability
          }));
          break;
        case 'crew':
          setCrewDateAvailability(prev => ({
            ...prev,
            [userId]: dateAvailability
          }));
          break;
        case 'manager':
          setManagerDateAvailability(prev => ({
            ...prev,
            [userId]: dateAvailability
          }));
          break;
        case 'organization':
          setOrganizationDateAvailability(prev => ({
            ...prev,
            [userId]: dateAvailability
          }));
          break;
        default:
          break;
      }
    } catch (error) {
      console.error(`Error checking availability for user ${userId}:`, error);
    }
  };

  const handleAddArtist = (artist) => {
    if (!isUserInvited(artist.id)) {
      setSelectedArtists(prevSelectedArtists => [...prevSelectedArtists, {
        userId: artist.id,
        fullName: artist.fullName,
        profilePictureUrl: artist.profilePictureUrl,
        userSubType: artist.userSubType,
        email: artist.email,
        userName: artist.userName,
        idVerificationStatus: artist.idVerificationStatus,
      }]);
      
      // Initialize empty date assignments for this artist
      setArtistDateAssignments(prevAssignments => ({
        ...prevAssignments,
        [artist.id]: []
      }));
      
      // Initialize default permissions for this artist
      setArtistPermissions(prevPermissions => ({
        ...prevPermissions,
        [artist.id]: { ...roleDefaultPermissions.artist }
      }));
      
      // Check artist availability
      checkUserAvailability(artist.id, 'artist');
    }
    setArtistSearchQuery('');
    setArtistResults([]);
  };
  
  const handleAddCrew = (crew) => {
    if (!isUserInvited(crew.id)) {
      setSelectedCrew(prevSelectedCrew => [...prevSelectedCrew, {
        userId: crew.id,
        fullName: crew.fullName,
        profilePictureUrl: crew.profilePictureUrl,
        userSubType: crew.userSubType,
        email: crew.email,
        userName: crew.userName,
        idVerificationStatus: crew.idVerificationStatus,
      }]);
      
      // Initialize empty date assignments for this crew member
      setCrewDateAssignments(prevAssignments => ({
        ...prevAssignments,
        [crew.id]: []
      }));
      
      // Initialize default permissions for this crew member
      setCrewPermissions(prevPermissions => ({
        ...prevPermissions,
        [crew.id]: { ...roleDefaultPermissions.crew }
      }));
      
      // Check crew availability
      checkUserAvailability(crew.id, 'crew');
    }
    setCrewSearchQuery('');
    setCrewResults([]);
  };

  // Function to add a manager
  const handleAddManager = (manager) => {
    if (!isUserInvited(manager.id)) {
      setSelectedManagers(prevManagers => [...prevManagers, {
        userId: manager.id,
        fullName: manager.fullName,
        profilePictureUrl: manager.profilePictureUrl,
        userSubType: manager.userSubType,
        email: manager.email,
        userName: manager.userName,
        idVerificationStatus: manager.idVerificationStatus,
      }]);
      
      // Initialize empty date assignments and default permissions
      setManagerDateAssignments(prevAssignments => ({
        ...prevAssignments,
        [manager.id]: []
      }));
      
      setManagerPermissions(prevPermissions => ({
        ...prevPermissions,
        [manager.id]: { ...roleDefaultPermissions.manager }
      }));
      
      // Check manager availability
      checkUserAvailability(manager.id, 'manager');
    }
    setManagerSearchQuery('');
    setManagerResults([]);
  };

  // Function to add an organization
  const handleAddOrganization = (organization) => {
    if (!isUserInvited(organization.id)) {
      setSelectedOrganizations(prevOrgs => [...prevOrgs, {
        userId: organization.id,
        fullName: organization.fullName,
        profilePictureUrl: organization.profilePictureUrl,
        userSubType: organization.userSubType,
        email: organization.email,
        userName: organization.userName,
        idVerificationStatus: organization.idVerificationStatus,
      }]);
      
      // Initialize empty date assignments and default permissions
      setOrganizationDateAssignments(prevAssignments => ({
        ...prevAssignments,
        [organization.id]: []
      }));
      
      setOrganizationPermissions(prevPermissions => ({
        ...prevPermissions,
        [organization.id]: { ...roleDefaultPermissions.organization }
      }));
      
      // Check organization availability
      checkUserAvailability(organization.id, 'organization');
    }
    setOrganizationSearchQuery('');
    setOrganizationResults([]);
  };
  
  const handleRemoveArtist = (userId) => {
    setSelectedArtists(prev => prev.filter(artist => artist.userId !== userId));
    setArtistDateAssignments(prev => {
      const newAssignments = { ...prev };
      delete newAssignments[userId];
      return newAssignments;
    });
    setArtistPermissions(prev => {
      const newPermissions = { ...prev };
      delete newPermissions[userId];
      return newPermissions;
    });
    // Also remove availability data
    setArtistDateAvailability(prev => {
      const newAvailability = { ...prev };
      delete newAvailability[userId];
      return newAvailability;
    });
  };
  
  const handleRemoveCrew = (userId) => {
    setSelectedCrew(prev => prev.filter(crew => crew.userId !== userId));
    setCrewDateAssignments(prev => {
      const newAssignments = { ...prev };
      delete newAssignments[userId];
      return newAssignments;
    });
    setCrewPermissions(prev => {
      const newPermissions = { ...prev };
      delete newPermissions[userId];
      return newPermissions;
    });
    // Also remove availability data
    setCrewDateAvailability(prev => {
      const newAvailability = { ...prev };
      delete newAvailability[userId];
      return newAvailability;
    });
  };

  // Function to remove a manager
  const handleRemoveManager = (userId) => {
    setSelectedManagers(prev => prev.filter(manager => manager.userId !== userId));
    setManagerDateAssignments(prev => {
      const newAssignments = { ...prev };
      delete newAssignments[userId];
      return newAssignments;
    });
    setManagerPermissions(prev => {
      const newPermissions = { ...prev };
      delete newPermissions[userId];
      return newPermissions;
    });
    // Also remove availability data
    setManagerDateAvailability(prev => {
      const newAvailability = { ...prev };
      delete newAvailability[userId];
      return newAvailability;
    });
  };

  // Function to remove an organization
  const handleRemoveOrganization = (userId) => {
    setSelectedOrganizations(prev => prev.filter(org => org.userId !== userId));
    setOrganizationDateAssignments(prev => {
      const newAssignments = { ...prev };
      delete newAssignments[userId];
      return newAssignments;
    });
    setOrganizationPermissions(prev => {
      const newPermissions = { ...prev };
      delete newPermissions[userId];
      return newPermissions;
    });
    // Also remove availability data
    setOrganizationDateAvailability(prev => {
      const newAvailability = { ...prev };
      delete newAvailability[userId];
      return newAvailability;
    });
  };

  // Generic function to toggle a permission setting
  const togglePermission = (userId, permission, role) => {
    let permissionSetter;
    let permissionState;
    
    switch (role) {
      case 'artist':
        permissionSetter = setArtistPermissions;
        permissionState = artistPermissions;
        break;
      case 'crew':
        permissionSetter = setCrewPermissions;
        permissionState = crewPermissions;
        break;
      case 'manager':
        permissionSetter = setManagerPermissions;
        permissionState = managerPermissions;
        break;
      case 'organization':
        permissionSetter = setOrganizationPermissions;
        permissionState = organizationPermissions;
        break;
      case 'band':
        permissionSetter = setBandPermissions;
        permissionState = bandPermissions;
        break;
      default:
        return; // Invalid role
    }
    
    permissionSetter(prev => ({
      ...prev,
      [userId]: {
        ...(prev[userId] || {}),
        [permission]: !(prev[userId] ? prev[userId][permission] : false)
      }
    }));
  };
  
  // Function to set all permissions based on a predefined level
  const setPermissionLevel = (userId, level, role) => {
    let permissionSetter;
    
    switch (role) {
      case 'artist':
        permissionSetter = setArtistPermissions;
        break;
      case 'crew':
        permissionSetter = setCrewPermissions;
        break;
      case 'manager':
        permissionSetter = setManagerPermissions;
        break;
      case 'organization':
        permissionSetter = setOrganizationPermissions;
        break;
      case 'band':
        permissionSetter = setBandPermissions;
        break;
      default:
        return; // Invalid role
    }
    
    permissionSetter(prev => ({
      ...prev,
      [userId]: { ...defaultPermissions[level] }
    }));
  };
  
  // Toggle the permissions settings display for a specific user
  const togglePermissionsDisplay = (userId) => {
    setShowPermissionSettings(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  // Check if date is available for a user
  const isDateAvailableForUser = (userId, date, role) => {
    const dateString = date.toISOString();
    let userAvailability;
    
    switch (role.toLowerCase()) {
      case 'artist':
        userAvailability = artistDateAvailability[userId] || {};
        break;
      case 'crew':
        userAvailability = crewDateAvailability[userId] || {};
        break;
      case 'manager':
        userAvailability = managerDateAvailability[userId] || {};
        break;
      case 'organization':
        userAvailability = organizationDateAvailability[userId] || {};
        break;
      case 'band':
        userAvailability = bandDateAvailability[userId] || {};
        break;
      default:
        return true;
    }
    
    return !userAvailability[dateString] || userAvailability[dateString].available;
  };

  // Function to handle select all dates for any user type
  const handleSelectAllDates = (userId, role) => {
    const allDates = selectedDates.map(date => date.toISOString());
    let dateAssignmentsSetter;
    let dateAssignmentsState;
    let dateAvailabilityState;
    
    switch (role.toLowerCase()) {
      case 'artist':
        dateAssignmentsSetter = setArtistDateAssignments;
        dateAssignmentsState = artistDateAssignments;
        dateAvailabilityState = artistDateAvailability;
        break;
      case 'crew':
        dateAssignmentsSetter = setCrewDateAssignments;
        dateAssignmentsState = crewDateAssignments;
        dateAvailabilityState = crewDateAvailability;
        break;
      case 'manager':
        dateAssignmentsSetter = setManagerDateAssignments;
        dateAssignmentsState = managerDateAssignments;
        dateAvailabilityState = managerDateAvailability;
        break;
      case 'organization':
        dateAssignmentsSetter = setOrganizationDateAssignments;
        dateAssignmentsState = organizationDateAssignments;
        dateAvailabilityState = organizationDateAvailability;
        break;
      case 'band':
        dateAssignmentsSetter = setBandDateAssignments;
        dateAssignmentsState = bandDateAssignments;
        dateAvailabilityState = bandDateAvailability;
        break;
      default:
        return;
    }
    
    // Filter out dates that are not available for this user
    const userAvailability = dateAvailabilityState[userId] || {};
    const availableDates = allDates.filter(dateString => {
      return !userAvailability[dateString] || userAvailability[dateString].available;
    });
    
    // Also consider confirmed dates that can't be unselected
    const confirmedDates = confirmedDatesByUser[userId] || [];
    const pendingDates = pendingDatesByUser[userId] || [];
    
    // Filter out pending dates as they can't be modified
    const selectableDates = availableDates.filter(date => !pendingDates.includes(date));
    
    // Determine if we should select all or deselect all
    const currentAssignments = dateAssignmentsState[userId] || [];
    const shouldSelectAll = currentAssignments.length !== selectableDates.length;
    
    if (shouldSelectAll) {
      // Add all available dates
      dateAssignmentsSetter(prev => ({
        ...prev,
        [userId]: selectableDates
      }));
    } else {
      // Remove all except confirmed/pending dates
      dateAssignmentsSetter(prev => ({
        ...prev,
        [userId]: confirmedDates.concat(pendingDates)
      }));
    }
  };

  // Modified handler for date selection
  const handleDateSelection = (userId, date, role) => {
    const dateString = date.toISOString();
    let dateAssignmentsSetter;
    let dateAssignmentsState;
    let dateAvailabilityState;
    
    // Determine which state to update based on role
    switch (role.toLowerCase()) {
      case 'artist':
        dateAssignmentsSetter = setArtistDateAssignments;
        dateAssignmentsState = artistDateAssignments;
        dateAvailabilityState = artistDateAvailability;
        break;
      case 'crew':
        dateAssignmentsSetter = setCrewDateAssignments;
        dateAssignmentsState = crewDateAssignments;
        dateAvailabilityState = crewDateAvailability;
        break;
      case 'manager':
        dateAssignmentsSetter = setManagerDateAssignments;
        dateAssignmentsState = managerDateAssignments;
        dateAvailabilityState = managerDateAvailability;
        break;
      case 'organization':
        dateAssignmentsSetter = setOrganizationDateAssignments;
        dateAssignmentsState = organizationDateAssignments;
        dateAvailabilityState = organizationDateAvailability;
        break;
      case 'band':
        dateAssignmentsSetter = setBandDateAssignments;
        dateAssignmentsState = bandDateAssignments;
        dateAvailabilityState = bandDateAvailability;
        break;
      default:
        return;
    }
    
    const userAvailability = dateAvailabilityState[userId] || {};
    
    // Check if this date is already booked for this user
    if (userAvailability[dateString] && !userAvailability[dateString].available) {
      // Show conflict details
      const conflict = userAvailability[dateString].conflictDetails;
      setConflictDetails({
        date,
        eventName: conflict.eventName,
        venueDisplayName: conflict.venueDisplayName,
        venueAddress: conflict.venueAddress
      });
      setShowDateConflictModal(true);
      return;
    }
    
    const confirmedDates = confirmedDatesByUser[userId] || [];
    const pendingDates = pendingDatesByUser[userId] || [];
    const userDates = dateAssignmentsState[userId] || [];
    const userUnselectedConfirmed = unselectedConfirmedDates[userId] || [];
  
    // Don't allow changes to pending dates
    if (pendingDates.includes(dateString)) {
      return;
    }
  
    // Handle confirmed dates
    if (confirmedDates.includes(dateString)) {
      const wasUnselected = unselectedConfirmedDates[userId]?.includes(dateString);
      
      if (!wasUnselected) {
        // First time unselecting a confirmed date
        setUnselectedConfirmedDates(prev => ({
          ...prev,
          [userId]: [...(prev[userId] || []), dateString]
        }));
        setRemovedConfirmedDates(prev => ({
          ...prev,
          [userId]: [...(prev[userId] || []), dateString]
        }));
      } else {
        // Reselecting a previously unselected confirmed date
        setUnselectedConfirmedDates(prev => ({
          ...prev,
          [userId]: prev[userId].filter(d => d !== dateString)
        }));
        setRemovedConfirmedDates(prev => ({
          ...prev,
          [userId]: prev[userId].filter(d => d !== dateString)
        }));
      }
    } else {
      // Handle regular date selection
      dateAssignmentsSetter(prev => {
        const currentDates = prev[userId] || [];
        return {
          ...prev,
          [userId]: currentDates.includes(dateString)
            ? currentDates.filter(d => d !== dateString)
            : [...currentDates, dateString]
        };
      });
    }
  };

  // Function to render permissions section
  const renderPermissions = (userId, permissions, role) => {
    const permissionKeys = [
      { key: 'canEditTourDetails', label: 'Edit Tour Details' },
      { key: 'canEditSchedule', label: 'Edit Schedule' },
      { key: 'canAddAttachments', label: 'Add Attachments' },
      { key: 'canAssignTasks', label: 'Assign Tasks' },
      { key: 'canEditSetlist', label: 'Edit Setlist' },
      { key: 'canManageGuestlist', label: 'Manage Guestlist' },
      { key: 'canManageHotels', label: 'Manage Hotels' },
      { key: 'canManageTickets', label: 'Manage Tickets' },
      { key: 'canAssignUsers', label: 'Assign Users' }
    ];

    // Determine the current permission level
    const getCurrentPermissionLevel = () => {
      if (JSON.stringify(permissions) === JSON.stringify(defaultPermissions.fullControl)) {
        return PERMISSION_LEVELS.FULL_CONTROL;
      } else if (JSON.stringify(permissions) === JSON.stringify(defaultPermissions.viewOnly)) {
        return PERMISSION_LEVELS.VIEW_ONLY;
      } else {
        return PERMISSION_LEVELS.COLLAB;
      }
    };

    const currentLevel = getCurrentPermissionLevel();

    return (
      <div className={styles.permissionsContainer}>
        <div className={styles.permissionLevels}>
          <button 
            className={`${styles.permissionLevelButton} ${currentLevel === PERMISSION_LEVELS.FULL_CONTROL ? styles.active : ''}`}
            onClick={() => setPermissionLevel(userId, PERMISSION_LEVELS.FULL_CONTROL, role)}
          >
            <FontAwesomeIcon icon={faLock} />
            <span>Full Control</span>
          </button>
          <button 
            className={`${styles.permissionLevelButton} ${currentLevel === PERMISSION_LEVELS.VIEW_ONLY ? styles.active : ''}`}
            onClick={() => setPermissionLevel(userId, PERMISSION_LEVELS.VIEW_ONLY, role)}
          >
            <FontAwesomeIcon icon={faLockOpen} />
            <span>View Only</span>
          </button>
          <button 
            className={`${styles.permissionLevelButton} ${currentLevel === PERMISSION_LEVELS.COLLAB ? styles.active : ''}`}
            onClick={() => setPermissionLevel(userId, PERMISSION_LEVELS.COLLAB, role)}
          >
            <FontAwesomeIcon icon={faUsers} />
            <span>Collaboration</span>
          </button>
        </div>
        
        <div className={styles.permissionsList}>
          {permissionKeys.map(({ key, label }) => (
            <div key={key} className={styles.permissionItem}>
              <span className={styles.permissionLabel}>{label}</span>
              <button 
                className={`${styles.permissionToggle} ${permissions[key] ? styles.active : ''}`}
                onClick={() => togglePermission(userId, key, role)}
              >
                <FontAwesomeIcon icon={permissions[key] ? faToggleOn : faToggleOff} />
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render member card with permissions toggle
  const renderMemberCard = (member, role) => {
    const userId = member.userId;
    let dateAssignments;
    let userPermissions;
    let userAvailability;
    
    // Determine which state to use based on role
    switch (role.toLowerCase()) {
      case 'artist':
        dateAssignments = artistDateAssignments[userId] || [];
        userPermissions = artistPermissions[userId] || roleDefaultPermissions.artist;
        userAvailability = artistDateAvailability[userId] || {};
        break;
      case 'crew':
        dateAssignments = crewDateAssignments[userId] || [];
        userPermissions = crewPermissions[userId] || roleDefaultPermissions.crew;
        userAvailability = crewDateAvailability[userId] || {};
        break;
      case 'manager':
        dateAssignments = managerDateAssignments[userId] || [];
        userPermissions = managerPermissions[userId] || roleDefaultPermissions.manager;
        userAvailability = managerDateAvailability[userId] || {};
        break;
      case 'organization':
        dateAssignments = organizationDateAssignments[userId] || [];
        userPermissions = organizationPermissions[userId] || roleDefaultPermissions.organization;
        userAvailability = organizationDateAvailability[userId] || {};
        break;
      default:
        dateAssignments = [];
        userPermissions = {};
        userAvailability = {};
    }
    
    const confirmedDates = confirmedDatesByUser[userId] || [];
    const pendingDates = pendingDatesByUser[userId] || [];
    const userUnselectedConfirmed = unselectedConfirmedDates[userId] || [];
    const showSettings = showPermissionSettings[userId] || false;
  
    return (
      <div key={userId} className={styles.selectedMemberCard}>
        <img
          src={member.profilePictureUrl || defaultAvatar}
          alt={member.fullName}
          className={styles.memberPhoto}
        />
        <div className={styles.memberInfo}>
          <div className={styles.memberNameContainer}>
            <span className={styles.memberName}>{member.fullName}</span>
            {member.idVerificationStatus === 'Verified' && (
              <img src={verifiedBadge} alt="Verified" className={styles.verifiedBadge} />
            )}
          </div>
          <span className={styles.userName}>@{member.userName}</span>
          <span className={styles.userSubType}>{member.userSubType}</span>
          
          <button 
            className={styles.permissionsButton}
            onClick={() => togglePermissionsDisplay(userId)}
          >
            <FontAwesomeIcon icon={faCog} />
            <span>{showSettings ? 'Hide Permissions' : 'Manage Permissions'}</span>
          </button>
        </div>
        
        {showSettings && (
          renderPermissions(userId, userPermissions, role)
        )}
        
        <div className={styles.dateSelectionContainer}>
          <button 
            className={styles.selectAllButton}
            onClick={() => handleSelectAllDates(userId, role)}
          >
            Select All Available
          </button>
          <div className={styles.datesScrollWrapper}>
            <div className={styles.datesScrollContainer}>
              {selectedDates.map(date => {
                const dateString = date.toISOString();
                const isConfirmed = confirmedDates.includes(dateString);
                const isPending = pendingDates.includes(dateString);
                const isSelected = dateAssignments.includes(dateString);
                const wasUnselected = userUnselectedConfirmed.includes(dateString);
                const isAvailable = isDateAvailableForUser(userId, date, role);
                
                let dateClassName = styles.dateCard;
                let statusLabel = '';
                
                // Determine date status class and label
                if (isConfirmed && !wasUnselected) {
                  dateClassName += ' ' + styles.confirmedDate;
                  statusLabel = 'Confirmed';
                } else if (isPending) {
                  dateClassName += ' ' + styles.pendingDate;
                  statusLabel = 'Pending';
                } else if (!isAvailable) {
                  dateClassName += ' ' + styles.bookedDate;
                  statusLabel = 'Booked';
                } else if (isSelected) {
                  dateClassName += ' ' + styles.selected;
                  statusLabel = 'Selected';
                }
  
                return (
                  <div
                    key={dateString}
                    className={dateClassName}
                    onClick={() => !isPending && isAvailable && handleDateSelection(userId, date, role)}
                  >
                    <div className={styles.dateCardDay}>
                      {date.toLocaleDateString('en-GB', { weekday: 'short' })}
                    </div>
                    <div className={styles.dateCardDate}>
                      {date.toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      })}
                    </div>
                    {statusLabel && (
                      <div className={styles.dateStatus}>{statusLabel}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
  
        {!confirmedDates.length && !pendingDates.length && (
          <button
            onClick={() => {
              switch (role.toLowerCase()) {
                case 'artist':
                  handleRemoveArtist(userId);
                  break;
                case 'crew':
                  handleRemoveCrew(userId);
                  break;
                case 'manager':
                  handleRemoveManager(userId);
                  break;
                case 'organization':
                  handleRemoveOrganization(userId);
                  break;
                default:
                  break;
              }
            }}
            className={styles.removeMemberButton}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        )}
      </div>
    );
  };

  // Function to render band card with permissions
  const renderBandCard = (band) => {
    const bandId = band.id;
    const selectedDatesForBand = bandDateAssignments[bandId] || [];
    const permissions = bandPermissions[bandId] || roleDefaultPermissions.band;
    const showSettings = showPermissionSettings[bandId] || false;
    const bandAvailability = bandDateAvailability[bandId] || {};

    return (
      <div key={bandId} className={styles.selectedMemberCard}>
        <img
          src={band.bandProfilePictureUrl || defaultBandPic}
          alt={band.bandFullName}
          className={styles.memberPhoto}
        />
        <div className={styles.memberInfo}>
          <div className={styles.memberNameContainer}>
            <span className={styles.memberName}>{band.bandFullName}</span>
          </div>
          <span className={styles.userName}>@{band.bandUserName}</span>
          {band.bandGenres && (
            <div className={styles.genreContainer}>
              {band.bandGenres.slice(0, 2).map(genre => (
                <span key={genre} className={styles.genreTag}>{genre}</span>
              ))}
            </div>
          )}
          
          <button 
            className={styles.permissionsButton}
            onClick={() => togglePermissionsDisplay(bandId)}
          >
            <FontAwesomeIcon icon={faCog} />
            <span>{showSettings ? 'Hide Permissions' : 'Manage Permissions'}</span>
          </button>
        </div>
        
        {showSettings && (
          renderPermissions(bandId, permissions, 'band')
        )}
        
        <div className={styles.dateSelectionContainer}>
          <button 
            className={styles.selectAllButton}
            onClick={() => handleSelectAllDatesForBand(bandId)}
          >
            Select All Available
          </button>
          <div className={styles.datesScrollWrapper}>
                <div className={styles.datesScrollContainer}>
                  {selectedDates.map(date => {
                    const dateString = date.toISOString();
                    const isSelected = selectedDatesForBand.includes(dateString);
                    const isAvailable = !bandAvailability[dateString] || bandAvailability[dateString].available;
                    
                    let dateClassName = `${styles.dateCard} ${isSelected ? styles.selected : ''}`;
                    let statusLabel = '';
                    
                    if (!isAvailable) {
                      dateClassName += ` ${styles.bookedDate}`;
                      statusLabel = 'Booked';
                    } else if (isSelected) {
                      statusLabel = 'Selected';
                    }

                    return (
                      <div
                        key={dateString}
                        className={dateClassName}
                        onClick={() => isAvailable && handleBandDateSelection(bandId, date)}
                      >
                        <div className={styles.dateCardDay}>
                          {date.toLocaleDateString('en-GB', { weekday: 'short' })}
                        </div>
                        <div className={styles.dateCardDate}>
                          {date.toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          })}
                        </div>
                        {statusLabel && (
                          <div className={styles.dateStatus}>{statusLabel}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <button
              onClick={() => handleRemoveBand(bandId)}
              className={styles.removeMemberButton}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        );
      };

  // Updated function to handle form submission
  const handleSubmit = async () => {
    try {
      setLoading(true);
      setLoadingMessage('Updating event details...');
  
      // Format date as YYYY-MM-DD consistently across all usages
      const formatLocalDate = (d) => {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
  
      const db = getFirestoreInstance();
      // Create a batch for atomic operations
      const batch = writeBatch(db);
      
      // Determine if we're editing or creating
      const isEditing = !!location.state?.tourData;
      
      // Create document in the appropriate collection based on event type
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      let eventId = isEditing ? location.state.tourData.id : doc(collection(db, collectionName)).id;
      let mainEventRef = doc(db, collectionName, eventId);
  
      // Handle cover photo
      let updatedCoverPhotoUrl = coverPhotoUrl || '';
      if (coverPhoto) {
        const storage = getStorage();
        const storageRef = ref(storage, `${collectionName}/${eventId}/coverPhoto/cover.jpg`);
        const uploadTask = uploadBytesResumable(storageRef, coverPhoto);
        const snapshot = await uploadTask;
        updatedCoverPhotoUrl = await getDownloadURL(snapshot.ref);
      }
  
      // If editing, get existing data
      let existingEventData = null;
      if (isEditing) {
        const eventSnapshot = await getDoc(mainEventRef);
        if (eventSnapshot.exists()) {
          existingEventData = eventSnapshot.data();
        }
      }
  
      setLoadingMessage('Processing user assignments...');
  
      // Prepare tour date references
      const eventDates = selectedDates.map((date, index) => ({
        dateDocId: `date${index + 1}`,
        date: formatLocalDate(date)
      }));
  
      // Helper function to safely convert date to ISO string
      const safeToISOString = (date) => {
        if (!date) return null;
        if (typeof date === 'string') return date;
        if (date instanceof Date) return date.toISOString();
        return null;
      };
  
      // Helper function to safely map dates
      const safeMapDates = (dateStr) => {
        if (!dateStr) return null;
        const dateIndex = selectedDates.findIndex(d => {
          // Use formatLocalDate instead of ISO string for comparison
          const formattedDate = formatLocalDate(d);
          return formattedDate === dateStr || safeToISOString(d) === dateStr;
        });
        
        if (dateIndex === -1) return null;
        
        return {
          date: formatLocalDate(selectedDates[dateIndex]), // Use consistent YYYY-MM-DD format
          dateDocId: `date${dateIndex + 1}`
        };
      };
      
      // Creator info for reuse in invitations
      const creatorInfo = {
        userId: userData.userId || '',
        fullName: userData.fullName || '',
        userName: userData.userName || '',
        email: userData.email || '',
        profilePictureUrl: userData.profilePictureUrl || '',
        userType: userData.userType || '',
        userSubType: userData.userSubType || ''
      };
  
      // Debugging log for venue details
      console.log('Saving venue details:', venueDetails);
      for (let i = 0; i < selectedDates.length; i++) {
        const date = selectedDates[i];
        const dateKey = date.getTime().toString();
        const venue = venueDetails[dateKey];
        console.log(`Date ${formatLocalDate(date)} venue:`, venue?.displayName || 'No venue');
      }
  
      // Track invitations to add to main document reference
      const pendingInvitationIds = [];
  
      // Process artists - Create pendingInvitations in subcollection
      for (const artist of selectedArtists) {
        const userId = artist.userId || '';
        if (!userId) continue; // Skip if no valid userId
        
        // Ensure all dates are valid
        const selectedDatesForUser = (artistDateAssignments[userId] || []).filter(date => !!date);
        const confirmedDates = (confirmedDatesByUser[userId] || []).filter(date => !!date);
        const pendingDates = (pendingDatesByUser[userId] || []).filter(date => !!date);
        const unselectedConfirmedDates = (removedConfirmedDates[userId] || []).filter(date => !!date);
        const permissions = artistPermissions[userId] || roleDefaultPermissions.artist;
  
        // Handle removed confirmed dates
        if (unselectedConfirmedDates.length > 0) {
          // Create notification for removed dates
          const notification = {
            userId,
            tourId: eventId,
            tourName: eventName || '',
            dates: unselectedConfirmedDates,
            type: 'REMOVED_FROM_DATES',
            message: `You have been removed from ${unselectedConfirmedDates.length} date(s) in the ${eventType} "${eventName || ''}"`
          };
          await sendEventRemovalNotification(userId, notification);
        }
  
        // Filter out dates that are already confirmed or pending
        const newDates = selectedDatesForUser.filter(date => 
          date && !confirmedDates.includes(date) && !pendingDates.includes(date)
        );
  
        if (newDates.length > 0) {
          // Map dates to the appropriate format
          const mappedDates = newDates.map(dateStr => safeMapDates(dateStr)).filter(d => !!d);
  
          if (mappedDates.length > 0) {
            // Create invitation data
            const invitationData = {
              userId: artist.userId || '',
              userName: artist.userName || '',
              fullName: artist.fullName || '',
              email: artist.email || '',
              profilePictureUrl: artist.profilePictureUrl || '',
              userType: 'Artist',
              userSubType: artist.userSubType || '',
              role: 'Artist',
              permissions,
              assignedDates: mappedDates,
              status: 'pending',
              timestamp: serverTimestamp(),
              eventType: eventType,
              tourId: eventId,
              tourName: eventName,
              showName: eventType === 'show' ? eventName : null,
              actName: actName,
              actType: actType
            };
  
            // Create a unique ID for this invitation
            const invitationId = crypto.randomUUID();
            pendingInvitationIds.push(invitationId);
            
            // Add to pendingInvitations subcollection
            const invitationRef = doc(db, collectionName, eventId, 'pendingInvitations', invitationId);
            batch.set(invitationRef, {
              id: invitationId,
              ...invitationData,
              createdBy: creatorInfo
            });
            
            // IMPORTANT: Also add to user's eventInvitations subcollection
            const userInvitationRef = doc(db, 'users', userId, 'eventInvitations', invitationId);
            batch.set(userInvitationRef, {
              id: invitationId,
              type: eventType === 'tour' ? NOTIFICATION_TYPES.TOUR_INVITATION : NOTIFICATION_TYPES.SHOW_INVITATION,
              title: `New ${eventType === 'tour' ? 'Tour' : 'Show'} Invitation`,
              message: `You've been invited to join ${eventType === 'show' ? 'the show' : 'the tour'} "${eventName}"`,
              status: 'pending',
              timestamp: serverTimestamp(),
              ...invitationData
            });
            
            // Queue notification to be sent after batch is committed
            setTimeout(() => {
              sendEventInvitationNotification(userId, {
                ...invitationData,
                invitationId,
                tourId: eventId,
                eventType: eventType,
                tourName: eventName,
                showName: eventType === 'show' ? eventName : null,
                createdBy: creatorInfo
              });
            }, 500);
          }
        }
      }
  
      // Process crew members - Create pendingInvitations in subcollection
      for (const crew of selectedCrew) {
        const userId = crew.userId || '';
        if (!userId) continue; // Skip if no valid userId
        
        // Ensure all dates are valid
        const selectedDatesForUser = (crewDateAssignments[userId] || []).filter(date => !!date);
        const confirmedDates = (confirmedDatesByUser[userId] || []).filter(date => !!date);
        const pendingDates = (pendingDatesByUser[userId] || []).filter(date => !!date);
        const unselectedConfirmedDates = (removedConfirmedDates[userId] || []).filter(date => !!date);
        const permissions = crewPermissions[userId] || roleDefaultPermissions.crew;
  
        // Handle removed confirmed dates
        if (unselectedConfirmedDates.length > 0) {
          // Create notification for removed dates
          const notification = {
            userId,
            tourId: eventId,
            tourName: eventName || '',
            dates: unselectedConfirmedDates,
            type: 'REMOVED_FROM_DATES',
            message: `You have been removed from ${unselectedConfirmedDates.length} date(s) in the ${eventType} "${eventName || ''}"`
          };
          await sendEventRemovalNotification(userId, notification);
        }
  
        // Filter out dates that are already confirmed or pending
        const newDates = selectedDatesForUser.filter(date => 
          date && !confirmedDates.includes(date) && !pendingDates.includes(date)
        );
  
        if (newDates.length > 0) {
          // Map dates to the appropriate format
          const mappedDates = newDates.map(dateStr => safeMapDates(dateStr)).filter(d => !!d);
  
          if (mappedDates.length > 0) {
            // Create invitation data
            const invitationData = {
              userId: crew.userId || '',
              userName: crew.userName || '',
              fullName: crew.fullName || '',
              email: crew.email || '',
              profilePictureUrl: crew.profilePictureUrl || '',
              userType: 'Crew',
              userSubType: crew.userSubType || '',
              role: 'Crew',
              permissions,
              assignedDates: mappedDates,
              status: 'pending',
              timestamp: serverTimestamp(),
              eventType: eventType,
              tourId: eventId,
              tourName: eventName,
              showName: eventType === 'show' ? eventName : null,
              actName: actName,
              actType: actType
            };
  
            // Create a unique ID for this invitation
            const invitationId = crypto.randomUUID();
            pendingInvitationIds.push(invitationId);
            
            // Add to pendingInvitations subcollection
            const invitationRef = doc(db, collectionName, eventId, 'pendingInvitations', invitationId);
            batch.set(invitationRef, {
              id: invitationId,
              ...invitationData,
              createdBy: creatorInfo
            });
            
            // Also add to user's eventInvitations subcollection
            const userInvitationRef = doc(db, 'users', userId, 'eventInvitations', invitationId);
            batch.set(userInvitationRef, {
              id: invitationId,
              type: eventType === 'tour' ? NOTIFICATION_TYPES.TOUR_INVITATION : NOTIFICATION_TYPES.SHOW_INVITATION,
              title: `New ${eventType === 'tour' ? 'Tour' : 'Show'} Invitation`,
              message: `You've been invited to join ${eventType === 'show' ? 'the show' : 'the tour'} "${eventName}"`,
              status: 'pending',
              timestamp: serverTimestamp(),
              ...invitationData
            });
            
            // Queue notification to be sent after batch is committed
            setTimeout(() => {
              sendEventInvitationNotification(userId, {
                ...invitationData,
                invitationId,
                tourId: eventId,
                eventType: eventType,
                tourName: eventName,
                showName: eventType === 'show' ? eventName : null,
                createdBy: creatorInfo
              });
            }, 500);
          }
        }
      }
  
      // Process managers - Create pendingInvitations in subcollection
      for (const manager of selectedManagers) {
        const userId = manager.userId || '';
        if (!userId) continue; // Skip if no valid userId
        
        // Ensure all dates are valid
        const selectedDatesForUser = (managerDateAssignments[userId] || []).filter(date => !!date);
        const confirmedDates = (confirmedDatesByUser[userId] || []).filter(date => !!date);
        const pendingDates = (pendingDatesByUser[userId] || []).filter(date => !!date);
        const unselectedConfirmedDates = (removedConfirmedDates[userId] || []).filter(date => !!date);
        const permissions = managerPermissions[userId] || roleDefaultPermissions.manager;
  
        // Handle removed confirmed dates
        if (unselectedConfirmedDates.length > 0) {
          // Create notification for removed dates
          const notification = {
            userId,
            tourId: eventId,
            tourName: eventName || '',
            dates: unselectedConfirmedDates,
            type: 'REMOVED_FROM_DATES',
            message: `You have been removed from ${unselectedConfirmedDates.length} date(s) in the ${eventType} "${eventName || ''}"`
          };
          await sendEventRemovalNotification(userId, notification);
        }
  
        // Filter out dates that are already confirmed or pending
        const newDates = selectedDatesForUser.filter(date => 
          date && !confirmedDates.includes(date) && !pendingDates.includes(date)
        );
  
        if (newDates.length > 0) {
          // Map dates to the appropriate format
          const mappedDates = newDates.map(dateStr => safeMapDates(dateStr)).filter(d => !!d);
  
          if (mappedDates.length > 0) {
            // Create invitation data
            const invitationData = {
              userId: manager.userId || '',
              userName: manager.userName || '',
              fullName: manager.fullName || '',
              email: manager.email || '',
              profilePictureUrl: manager.profilePictureUrl || '',
              userType: 'Manager',
              userSubType: manager.userSubType || '',
              role: 'Manager',
              permissions,
              assignedDates: mappedDates,
              status: 'pending',
              timestamp: serverTimestamp(),
              eventType: eventType,
              tourId: eventId,
              tourName: eventName,
              showName: eventType === 'show' ? eventName : null,
              actName: actName,
              actType: actType
            };
  
            // Create a unique ID for this invitation
            const invitationId = crypto.randomUUID();
            pendingInvitationIds.push(invitationId);
            
            // Add to pendingInvitations subcollection
            const invitationRef = doc(db, collectionName, eventId, 'pendingInvitations', invitationId);
            batch.set(invitationRef, {
              id: invitationId,
              ...invitationData,
              createdBy: creatorInfo
            });
            
            // Also add to user's eventInvitations subcollection
            const userInvitationRef = doc(db, 'users', userId, 'eventInvitations', invitationId);
            batch.set(userInvitationRef, {
              id: invitationId,
              type: eventType === 'tour' ? NOTIFICATION_TYPES.TOUR_INVITATION : NOTIFICATION_TYPES.SHOW_INVITATION,
              title: `New ${eventType === 'tour' ? 'Tour' : 'Show'} Invitation`,
              message: `You've been invited to join ${eventType === 'show' ? 'the show' : 'the tour'} "${eventName}"`,
              status: 'pending',
              timestamp: serverTimestamp(),
              ...invitationData
            });
            
            // Queue notification to be sent after batch is committed
            setTimeout(() => {
              sendEventInvitationNotification(userId, {
                ...invitationData,
                invitationId,
                tourId: eventId,
                eventType: eventType,
                tourName: eventName,
                showName: eventType === 'show' ? eventName : null,
                createdBy: creatorInfo
              });
            }, 500);
          }
        }
      }
  
      // Process organizations - Create pendingInvitations in subcollection
      for (const organization of selectedOrganizations) {
        const userId = organization.userId || '';
        if (!userId) continue; // Skip if no valid userId
        
        // Ensure all dates are valid
        const selectedDatesForUser = (organizationDateAssignments[userId] || []).filter(date => !!date);
        const confirmedDates = (confirmedDatesByUser[userId] || []).filter(date => !!date);
        const pendingDates = (pendingDatesByUser[userId] || []).filter(date => !!date);
        const unselectedConfirmedDates = (removedConfirmedDates[userId] || []).filter(date => !!date);
        const permissions = organizationPermissions[userId] || roleDefaultPermissions.organization;
  
        // Handle removed confirmed dates
        if (unselectedConfirmedDates.length > 0) {
          // Create notification for removed dates
          const notification = {
            userId,
            tourId: eventId,
            tourName: eventName || '',
            dates: unselectedConfirmedDates,
            type: 'REMOVED_FROM_DATES',
            message: `You have been removed from ${unselectedConfirmedDates.length} date(s) in the ${eventType} "${eventName || ''}"`
          };
          await sendEventRemovalNotification(userId, notification);
        }
  
        // Filter out dates that are already confirmed or pending
        const newDates = selectedDatesForUser.filter(date => 
          date && !confirmedDates.includes(date) && !pendingDates.includes(date)
        );
  
        if (newDates.length > 0) {
          // Map dates to the appropriate format
          const mappedDates = newDates.map(dateStr => safeMapDates(dateStr)).filter(d => !!d);
  
          if (mappedDates.length > 0) {
            // Create invitation data
            const invitationData = {
              userId: organization.userId || '',
              userName: organization.userName || '',
              fullName: organization.fullName || '',
              email: organization.email || '',
              profilePictureUrl: organization.profilePictureUrl || '',
              userType: 'Organization',
              userSubType: organization.userSubType || '',
              role: 'Organization',
              permissions,
              assignedDates: mappedDates,
              status: 'pending',
              timestamp: serverTimestamp(),
              eventType: eventType,
              tourId: eventId,
              tourName: eventName,
              showName: eventType === 'show' ? eventName : null,
              actName: actName,
              actType: actType
            };
  
            // Create a unique ID for this invitation
            const invitationId = crypto.randomUUID();
            pendingInvitationIds.push(invitationId);
            
            // Add to pendingInvitations subcollection
            const invitationRef = doc(db, collectionName, eventId, 'pendingInvitations', invitationId);
            batch.set(invitationRef, {
              id: invitationId,
              ...invitationData,
              createdBy: creatorInfo
            });
            
            // Also add to user's eventInvitations subcollection
            const userInvitationRef = doc(db, 'users', userId, 'eventInvitations', invitationId);
            batch.set(userInvitationRef, {
              id: invitationId,
              type: eventType === 'tour' ? NOTIFICATION_TYPES.TOUR_INVITATION : NOTIFICATION_TYPES.SHOW_INVITATION,
              title: `New ${eventType === 'tour' ? 'Tour' : 'Show'} Invitation`,
              message: `You've been invited to join ${eventType === 'show' ? 'the show' : 'the tour'} "${eventName}"`,
              status: 'pending',
              timestamp: serverTimestamp(),
              ...invitationData
            });
            
            // Queue notification to be sent after batch is committed
            setTimeout(() => {
              sendEventInvitationNotification(userId, {
                ...invitationData,
                invitationId,
                tourId: eventId,
                eventType: eventType,
                tourName: eventName,
                showName: eventType === 'show' ? eventName : null,
                createdBy: creatorInfo
              });
            }, 500);
          }
        }
      }
  
      // Process bands - Create band invitations
      for (const band of selectedBands) {
        const bandId = band.id || '';
        if (!bandId) continue;
        
        const selectedDatesForBand = (bandDateAssignments[bandId] || []).filter(date => !!date);
        const permissions = bandPermissions[bandId] || roleDefaultPermissions.band;
      
        if (selectedDatesForBand.length > 0) {
          // Map dates to the appropriate format
          const mappedDates = selectedDatesForBand.map(dateStr => safeMapDates(dateStr)).filter(d => !!d);
      
          if (mappedDates.length > 0) {
            // Create invitation data for band
            const bandInvitationData = {
              bandId,
              bandName: band.bandFullName || '',
              bandUserName: band.bandUserName || '',
              bandProfilePictureUrl: band.bandProfilePictureUrl || '',
              bandGenres: band.bandGenres || [],
              permissions,
              assignedDates: mappedDates,
              status: 'pending',
              timestamp: serverTimestamp(),
              eventType: eventType,
              tourId: eventId,
              tourName: eventName,
              showName: eventType === 'show' ? eventName : null,
              actName: actName,
              actType: actType
            };
      
            // Create a unique ID for this invitation
            const invitationId = crypto.randomUUID();
            pendingInvitationIds.push(invitationId);
            
            // Add to pendingInvitations subcollection
            const invitationRef = doc(db, collectionName, eventId, 'pendingInvitations', invitationId);
            batch.set(invitationRef, {
              id: invitationId,
              ...bandInvitationData,
              createdBy: creatorInfo
            });
      
            // Get band details to find admin IDs and create notification
            setTimeout(async () => {
              try {
                // Get band details to find admin IDs
                const bandDoc = await getDoc(doc(db, 'bands', bandId));
                if (bandDoc.exists()) {
                  const bandData = bandDoc.data();
                  // Get admin members
                  const adminMembers = bandData.members?.filter(member => member.role === 'admin') || [];
                  
                  // Create notification data
                  const bandNotificationData = {
                    bandId,
                    type: 'TOUR_INVITATION',
                    title: 'New Tour Invitation',
                    message: `Your band has been invited to join the ${eventType} "${eventName || ''}"`,
                    recipientIds: adminMembers.map(admin => admin.userId || ''),
                    actionBy: creatorInfo,
                    data: {
                      ...bandInvitationData,
                      tourId: eventId,
                      eventType: eventType,
                      tourName: eventName,
                      showName: eventType === 'show' ? eventName : null,
                      _displayData: {
                        tourName: eventName,
                        showName: eventType === 'show' ? eventName : null,
                        actName: actName,
                        creatorName: creatorInfo.fullName,
                        creatorPhoto: creatorInfo.profilePictureUrl
                      }
                    }
                  };
                  
                  // Create the notification for the band using the proper function
                  await createBandNotification(bandNotificationData);
                }
              } catch (error) {
                console.error('Error processing band notification:', error);
              }
            }, 500);
          }
        }
      }
  
      setLoadingMessage('Updating event data...');
      
      // Prepare main event document data
      const eventDocData = {
        type: eventType || 'tour', // 'tour' or 'show'
        ...(updatedCoverPhotoUrl && { tourCoverPhotoUrl: updatedCoverPhotoUrl }),
        // Include formatted dates for quick reference
        eventDates,
        lastUpdated: serverTimestamp()
      };
      
      // Add type-specific fields
      if (eventType === 'tour') {
        eventDocData.tourName = eventName;
        eventDocData.actName = actName;
        eventDocData.actType = actType;
      } else {
        eventDocData.showName = eventName; // Use showName field for shows
        eventDocData.tourName = eventName; // Keep tourName for backwards compatibility
        eventDocData.actName = actName;
        eventDocData.actType = actType;
      }
      
      // Add creation data only for new events
      if (!isEditing) {
        // Add creator info to main document
        eventDocData.createdBy = creatorInfo;
        eventDocData.createdAt = serverTimestamp();
      }
  
      // First, clean up removed dates if editing
      if (isEditing) {
        try {
          const eventDatesRef = collection(db, collectionName, eventId, 'eventDates');
          const existingDatesSnapshot = await getDocs(eventDatesRef);
          
          // Convert selected dates to same format as stored dates
          const formattedSelectedDates = selectedDates.map(date => formatLocalDate(date));
  
          // Delete dates that are no longer selected
          for (const doc of existingDatesSnapshot.docs) {
            const storedDate = doc.data().date;
            if (!formattedSelectedDates.includes(storedDate)) {
              // Add delete operation to batch
              batch.delete(doc.ref);
            }
          }
        } catch (error) {
          console.error('Error cleaning up old dates:', error);
        }
      }
  
      setLoadingMessage('Creating event dates...');
  
      // Create or update tour dates in the batch
     for (let i = 0; i < selectedDates.length; i++) {
        const dateId = `date${i + 1}`;
        const dateRef = doc(collection(db, collectionName, eventId, 'eventDates'), dateId);
        const date = selectedDates[i];
        
        const dateKey = date.getTime().toString();
        const venueDetailForDate = venueDetails[dateKey];
        
        // Check if this date already exists and has a locked venue
        let venueLocked = false;
        if (isEditing) {
          const existingDate = tourData.eventDates.find(td => 
            new Date(td.date).getTime().toString() === dateKey
          );
          if (existingDate) {
            venueLocked = existingDate.venueLocked || existingDate.venueVerified || false;
          }
        }
        
        // Create the date document with venueLocked field at the top level only
        const dateDocument = {
          date: formatLocalDate(date),
          dateDocId: dateId,
          venueLocked: venueLocked // Store at document level
        };

        // Only add venue details if they exist, without duplicating venueLocked
        if (venueDetailForDate) {
          // Make sure we're storing the venue object properly, but NOT the venueLocked field
          const { venueLocked: _, ...venueWithoutLock } = venueDetailForDate; // Remove any venueLocked from venueData
          
          dateDocument.event = {
            venueDetails: {
              venueData: venueWithoutLock // Store venue data without the lock field
            }
          };
        }
        
        // Add date document to batch
        batch.set(dateRef, dateDocument);
      }
  
      // Add the main event document to the batch - don't include pendingInvitations array anymore
      if (isEditing) {
        batch.update(mainEventRef, eventDocData);
      } else {
        batch.set(mainEventRef, eventDocData);
      }
  
      // ONLY add creator to assignedUsers subcollection
      if (!isEditing) {
        const creatorRef = doc(db, collectionName, eventId, 'assignedUsers', userData.userId || '');
        const creatorDocData = {
          userId: userData.userId || '',
          role: userData.userType?.toLowerCase() || 'user',
          isCreator: true, // Add isCreator flag for the creator
          userDetails: {
            fullName: userData.fullName || '',
            userName: userData.userName || '',
            profilePictureUrl: userData.profilePictureUrl || '',
            email: userData.email || '',
            userType: userData.userType || '',
            userSubType: userData.userSubType || '',
            idVerificationStatus: userData.idVerificationStatus || 'Unverified'
          },
          assignedDates: eventDates.map(date => ({
            ...date,
            status: 'accepted'
          })),
          permissions: defaultPermissions.fullControl,
          addedBy: userData.userId || '',
          createdAt: serverTimestamp()
        };
        
        batch.set(creatorRef, creatorDocData);
      }
  
      // Commit the entire batch as an atomic operation
      await batch.commit();
      
      // Format the venue details for assigned events
      const eventVenueDetails = selectedDates.map((date, index) => {
        const dateKey = date.getTime().toString();
        const venueData = venueDetails[dateKey] || {};
        return {
          date: formatLocalDate(date), // Use formatLocalDate instead of toISOString()
          dateDocId: `date${index + 1}`,
        };
      });
  
      // Format assignedDates for the creator's assignedEvents entry
      const assignedDatesForCreator = eventVenueDetails.map(ev => ({
        dateDocId: ev.dateDocId,
        date: ev.date,
        status: 'accepted'
      }));
  
      // Add to creator's assignedEvents collection with the new structure - ONLY for the creator
      // Create a SINGLE document with ID matching the eventId
      const creatorRole = userData.userType?.toLowerCase() || 'user';
      const creatorEventRef = doc(db, 'users', userData.userId, 'assignedEvents', eventId);
  
      // Create event entry with all dates included in a single document
      await setDoc(creatorEventRef, {
        type: eventType,
        tourId: eventType === 'tour' ? eventId : null,
        showId: eventType === 'show' ? eventId : null,
        role: creatorRole,
        isCreator: true, // Add isCreator flag for the creator
        assignedDates: assignedDatesForCreator, // Include ALL dates in one array
        permissions: defaultPermissions.fullControl,
        status: 'accepted',
        assignedAt: serverTimestamp(),
        createdAt: serverTimestamp()
      });
      
      // Add event dates to the user's assignedEventDates collection
      for (const date of selectedDates) {
        const dateStr = formatLocalDate(date);
        const monthYear = dateStr.substring(0, 7); // Get YYYY-MM format
        const dateKey = date.getTime().toString();
        const venueDetailForDate = venueDetails[dateKey] || {};
        
        // Create or update the month document in assignedEventDates
        const assignedDateRef = doc(db, 'users', userData.userId, 'assignedEventDates', monthYear);
        
        try {
          // Get the current document if it exists
          const assignedDateDoc = await getDoc(assignedDateRef);
          
          if (assignedDateDoc.exists()) {
            // Update existing document
            const existingData = assignedDateDoc.data();
            const existingDates = existingData.dates || [];
            
            // Check if this date already exists
            const dateIndex = existingDates.findIndex(d => d.date === dateStr);
            
            if (dateIndex >= 0) {
              // Update existing date entry
              existingDates[dateIndex] = {
                ...existingDates[dateIndex],
                date: dateStr,
                eventId: eventId,
                eventType: eventType,
                eventName: eventName,
                venue: {
                  displayName: venueDetailForDate.displayName || '',
                  formattedAddress: venueDetailForDate.formattedAddress || ''
                }
              };
            } else {
              // Add new date entry
              existingDates.push({
                date: dateStr,
                eventId: eventId,
                eventType: eventType,
                eventName: eventName,
                venue: {
                  displayName: venueDetailForDate.displayName || '',
                  formattedAddress: venueDetailForDate.formattedAddress || ''
                }
              });
            }
            
            // Update the document
            await updateDoc(assignedDateRef, {
              dates: existingDates,
              lastUpdated: serverTimestamp()
            });
          } else {
            // Create new document
            await setDoc(assignedDateRef, {
              dates: [{
                date: dateStr,
                eventId: eventId,
                eventType: eventType,
                eventName: eventName,
                venue: {
                  displayName: venueDetailForDate.displayName || '',
                  formattedAddress: venueDetailForDate.formattedAddress || ''
                }
              }],
              createdAt: serverTimestamp(),
              lastUpdated: serverTimestamp()
            });
          }
        } catch (error) {
          console.error(`Error updating assignedEventDates for ${dateStr}:`, error);
        }
      }
      
      // Clear cache to ensure fresh data
      clearCache();
  
      setLoadingMessage('Almost done...');
  
      // Add timeout before navigation
      await new Promise(resolve => setTimeout(resolve, 1000));
  
      // Fetch the updated data
      await fetchEventsData(userData, 1);
  
      // Add timeout before navigation
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Navigate to the first date of the tour/show
      if (eventType === 'tour') {
        navigate(`/tour/${eventId}/date1`);
      } else {
        navigate(`/show/${eventId}/date1`);
      }
  
    } catch (error) {
      console.error('Error in tour/show operation:', error);
      alert(`Failed to ${isEditing ? 'update' : 'create'} ${eventType}. Please try again.`);
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };
   
 const LoadingOverlay = () => (
   <div className={styles.loadingOverlay}>
     <div className={styles.loadingContent}>
       <div className={styles.loadingSpinner} />
       <p className={styles.loadingMessage}>{loadingMessage || 'Processing...'}</p>
     </div>
   </div>
 );

 // Form validation
 const isStep1Valid = eventName && actName && actType;
 const isStep2Valid = selectedDates.length > 0;

 // Helper function to get the current step status
 const getStepStatus = (step) => {
   if (step < currentStep) return 'completed';
   if (step === currentStep) return 'active';
   return 'pending';
 };

 const renderStepContent = () => {
   switch (currentStep) {
     case STEPS.EVENT_TYPE:
       return (
         <div className={styles.formSection}>
           <h2 className={styles.sectionTitle}>Choose Event Type</h2>
           <div className={styles.eventTypeSelection}>
             <button 
               className={`${styles.eventTypeButton} ${eventType === 'tour' ? styles.active : ''}`}
               onClick={() => setEventType('tour')}
               disabled={isEditing} // Disable when editing existing event
             >
               <FontAwesomeIcon icon={faCalendar} size="3x" />
               <div className={styles.eventTypeLabel}>Tour</div>
               <div className={styles.eventTypeDescription}>
                 Create a tour with multiple dates across venues
               </div>
             </button>
             
             <button 
               className={`${styles.eventTypeButton} ${eventType === 'show' ? styles.active : ''}`}
               onClick={() => setEventType('show')}
               disabled={isEditing} // Disable when editing existing event
             >
               <FontAwesomeIcon icon={faMusic} size="3x" />
               <div className={styles.eventTypeLabel}>Show</div>
               <div className={styles.eventTypeDescription}>
                 Add a single show or performance
               </div>
             </button>
           </div>
           {isEditing && (
             <div className={styles.warningMessage}>
               <FontAwesomeIcon icon={faInfoCircle} />
               <span>Event type cannot be changed after creation.</span>
             </div>
           )}
         </div>
       );

     case STEPS.TOUR_DETAILS:
       return (
         <div className={styles.formSection}>
           <h2 className={styles.sectionTitle}>
             {eventType === 'tour' ? 'Tour Details' : 'Show Details'}
           </h2>
           
           <div className={styles.coverPhotoContainer} onClick={() => document.getElementById('coverPhotoInput').click()}>
             {coverPhotoPreview ? (
               <>
                 <img src={coverPhotoPreview} alt="Cover" className={styles.coverPhotoPreview} />
                 <div className={styles.uploadOverlay}>
                   <FontAwesomeIcon icon={faImage} size="2x" />
                   <span>Change Cover Photo</span>
                 </div>
               </>
             ) : coverPhotoUrl ? (
               <>
                 <img src={coverPhotoUrl} alt="Cover" className={styles.coverPhotoPreview} />
                 <div className={styles.uploadOverlay}>
                   <FontAwesomeIcon icon={faImage} size="2x" />
                   <span>Change Cover Photo</span>
                 </div>
               </>
             ) : (
               <div className={styles.uploadOverlay} style={{ opacity: 1 }}>
                 <FontAwesomeIcon icon={faImage} size="2x" />
                 <span>Upload Cover Photo</span>
               </div>
             )}
             <input
               id="coverPhotoInput"
               type="file"
               accept="image/*"
               onChange={handleFileChange}
               className={styles.hiddenInput}
               style={{ display: 'none' }}
             />
           </div>
 
           <div className={styles.inputGroup}>
             <label className={styles.inputLabel}>
               {eventType === 'tour' ? 'Tour Name' : 'Show Name'}
             </label>
             <input
               type="text"
               value={eventName}
               onChange={(e) => setEventName(e.target.value)}
               className={styles.input}
               placeholder={eventType === 'tour' ? "Enter tour name" : "Enter show name"}
             />
           </div>
 
           <div className={styles.inputGroup}>
             <label className={styles.inputLabel}>Act Name</label>
             <input
               type="text"
               value={actName}
               onChange={(e) => setActName(e.target.value)}
               className={styles.input}
               placeholder="Enter act name"
             />
           </div>
 
           <div className={styles.inputGroup}>
             <label className={styles.inputLabel}>Act Type</label>
             <select
               value={actType}
               onChange={(e) => setActType(e.target.value)}
               className={styles.select}
             >
               <option value="">Select act type</option>
               <option value="Band">Band</option>
               <option value="Solo">Solo</option>
             </select>
           </div>
         </div>
       );
 
     case STEPS.SELECT_DATES:
       return (
         <div className={styles.calendarContainer}>
           <div className={styles.membershipInfo}>
             {userData.membershipTier === 'Elite' ? (
               <p>Elite membership: No date limit</p>
             ) : (
               <p>{`${userData.membershipTier} membership: ${selectedDates.length}/${getDateLimit()} dates selected`}</p>
             )}
           </div>
 
           <div className={styles.calendarWrapper}>
           <Calendar
  onChange={handleDateClick}
  value={selectedDates}
  selectRange={false}
  showNeighboringMonth={false} // This hides days from neighboring months
  onActiveStartDateChange={({ activeStartDate }) => {
    // Update active month when user navigates
    setActiveMonth(activeStartDate);
  }}
  tileClassName={({ date }) => {
    // Compare dates using local components
    const isSelected = selectedDates.some(d => 
      d.getFullYear() === date.getFullYear() &&
      d.getMonth() === date.getMonth() &&
      d.getDate() === date.getDate()
    );
    
    const isAvailable = isDateAvailable(date);
    
    // Return multiple classes if needed
    if (isSelected) {
      return styles.selectedDate;
    } else if (!isAvailable) {
      return styles.bookedDate;
    }
    
    return null;
  }}
  tileContent={({ date, view }) => {
    // Only add content to day tiles
    if (view !== 'month') return null;
    
    // Check if the date is booked
    const isAvailable = isDateAvailable(date);
    
    if (!isAvailable) {
      return (
        <div className={styles.bookedDateIndicator}>
          <span>Booked</span>
        </div>
      );
    }
    
    return null;
  }}
  tileDisabled={({ date }) => {
    // Disable dates that are already booked
    if (!isDateAvailable(date)) {
      return true;
    }
    return false;
  }}
  minDate={new Date()}
/>
           </div>
 
           {selectedDates.length > 0 && (
             <div className={styles.selectedDates}>
               <h3 className={styles.selectedDatesTitle}>Selected Dates:</h3>
               {selectedDates
                 .sort((a, b) => a - b) // Sort dates chronologically
                 .map((date) => (
                   <div 
                     key={`selected-${date.getTime()}`}
                     className={styles.dateItem}
                   >
                     <span className={styles.dateText}>
                       {date.toLocaleDateString('en-US', {
                         weekday: 'short',
                         year: 'numeric',
                         month: 'short',
                         day: 'numeric'
                       })}
                     </span>
                     <button
                       onClick={() => handleDateClick(date)}
                       className={styles.removeDate}
                     >
                       <FontAwesomeIcon icon={faTrash} />
                     </button>
                   </div>
                 ))}
             </div>
           )}
         </div>
       );
 
       case STEPS.VENUE_DETAILS:
        return (
          <div className={styles.formSection}>
            <h2 className={styles.sectionTitle}>Venue Details</h2>
            
            {renderDateSelector()}
            
            {selectedDates[selectedDateIndex] && (
              <div className={styles.venueSection}>
                <h3 className={styles.venueDateTitle}>
                  {selectedDates[selectedDateIndex].toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </h3>
      
                {/* Venue search UI - only show if venue is not locked */}
                {!isVenueLocked(selectedDates[selectedDateIndex]) ? (
                  <div className={styles.venueSearchContainer}>
                    <div className={styles.searchInputContainer}>
                      <input
                        type="text"
                        placeholder="Search venue"
                        className={styles.venueSearchInput}
                        value={venueSearchQuery}
                        onChange={(e) => setVenueSearchQuery(e.target.value)}
                        onKeyDown={handleVenueSearchKeyDown}
                      />
                      <button 
                        className={styles.searchButton}
                        onClick={handleVenueSearch}
                        disabled={!venueSearchQuery.trim()}
                      >
                        <FontAwesomeIcon icon={faSearch} />
                        Search
                      </button>
                    </div>
      
                    {/* Venue results */}
                    {renderVenueResults()}
                  </div>
                ) : (
                  <div className={styles.venueLockMessage}>
                    <FontAwesomeIcon icon={faLock} className={styles.lockIcon} />
                    <p>This venue is locked because one or more users have verified this show. The venue cannot be changed.</p>
                  </div>
                )}
      
                {/* Selected venue display */}
                {renderVenueDetails()}
              </div>
            )}
      
            {/* Map Search Modal */}
            <CreateTourMapSearch
              show={showMapSearch}
              onHide={() => setShowMapSearch(false)}
              onSelect={(venue) => {
                handleVenueSelect(venue, selectedDates[selectedDateIndex]);
                setShowMapSearch(false);
              }}
              db={db}
            />
          </div>
        );
         
     // Band selection step
     case STEPS.BAND_SELECTION:
       return (
         <div className={styles.formSection}>
           <h2 className={styles.sectionTitle}>Select Bands</h2>
           <p className={styles.stepDescription}>
             Associate bands with this {eventType}. They will receive an invitation to join.
           </p>
           
           {/* Search Section */}
           <div className={styles.searchSection}>
             <div className={styles.searchBar}>
               <input
                 type="text"
                 placeholder="Search bands by name..."
                 value={bandSearchQuery}
                 onChange={(e) => setBandSearchQuery(e.target.value)}
                 onKeyDown={(e) => e.key === 'Enter' && handleBandSearch()}
                 className={styles.searchInput}
               />
               <button
                 className={styles.searchButton}
                 onClick={handleBandSearch}
                 disabled={!bandSearchQuery.trim() || searchingBands}
               >
                 <FontAwesomeIcon icon={faSearch} />
                 {searchingBands ? 'Searching...' : 'Search'}
               </button>
             </div>
             
             {/* Search Results */}
             {searchingBands ? (
               <div className={styles.noResults}>Searching...</div>
             ) : bandResults.length > 0 ? (
               <div className={styles.searchResults}>
                 {bandResults.map((band) => (
                   <div
                     key={band.id}
                     className={`${styles.searchResult} ${band.isAssigned && styles.disabledSearchResult}`}
                     onClick={() => !band.isAssigned && handleAddBand(band)}
                   >
                     <img
                       src={band.bandProfilePictureUrl || defaultBandPic}
                       alt={band.bandFullName}
                       className={styles.profilePhoto}
                     />
                     <div className={styles.userInfo}>
                       <span className={styles.fullName}>{band.bandFullName}</span>
                       <span className={styles.userName}>@{band.bandUserName}</span>
                       {band.bandGenres && band.bandGenres.length > 0 && (
                         <div className={styles.genreContainer}>
                           {band.bandGenres.slice(0, 2).map(genre => (
                             <span key={genre} className={styles.genreTag}>{genre}</span>
                           ))}
                           {band.bandGenres.length > 2 && (
                             <span className={styles.moreGenres}>+{band.bandGenres.length - 2}</span>
                           )}
                         </div>
                       )}
                     </div>
                     {band.isAssigned && (
                       <span className={styles.alreadyAssignedText}>Already Added</span>
                     )}
                   </div>
                 ))}
               </div>
             ) : bandSearchQuery && !searchingBands ? (
               <div className={styles.noResults}>No bands found</div>
             ) : null}
           </div>

           {/* Selected Bands List */}
           <div className={styles.selectedMembersSection}>
             {selectedBands.map(band => renderBandCard(band))}
           </div>
         </div>
       );
 
     case STEPS.ARTIST_SELECTION:
       return (
         <div className={styles.formSection}>
           <h2 className={styles.sectionTitle}>Add Artists</h2>
           
           {/* Search Section */}
           <div className={styles.searchSection}>
             <div className={styles.searchBar}>
               <input
                 type="text"
                 placeholder="Search artists by name..."
                 value={artistSearchQuery}
                 onChange={(e) => setArtistSearchQuery(e.target.value)}
                 onKeyDown={handleArtistSearchKeyDown}
                 className={styles.searchInput}
               />
               <button
                 className={styles.searchButton}
                 onClick={handleArtistSearch}
                 disabled={!artistSearchQuery.trim() || searchingArtists}
               >
                 <FontAwesomeIcon icon={faSearch} />
                 {searchingArtists ? 'Searching...' : 'Search'}
               </button>
             </div>
             
             {/* Search Results */}
             {searchingArtists ? (
               <div className={styles.noResults}>Searching...</div>
             ) : artistResults.length > 0 ? (
               <div className={styles.searchResults}>
                 {artistResults.map((artist) => {
                   const isAssigned = selectedArtists.some(a => a.userId === artist.id) ||
                                    tourData?.artistDetails?.some(a => a.userId === artist.id) ||
                                    tourData?.pendingInvitationsData?.some(inv => inv.userId === artist.id);
                   
                   return (
                     <div
                       key={artist.id}
                       className={`${styles.searchResult} ${isAssigned && styles.disabledSearchResult}`}
                       onClick={() => !isAssigned && handleAddArtist(artist)}
                     >
                       <img
                         src={artist.profilePictureUrl || defaultAvatar}
                         alt={artist.fullName}
                         className={styles.profilePhoto}
                       />
                       <div className={styles.userInfo}>
                         <div className={styles.nameContainer}>
                           <span className={styles.fullName}>{artist.fullName}</span>
                           {artist.idVerificationStatus === 'Verified' && (
                             <img src={verifiedBadge} alt="Verified" className={styles.verifiedBadge} />
                           )}
                         </div>
                         <span className={styles.userName}>@{artist.userName}</span>
                         <span className={styles.userSubType}>{artist.userSubType}</span>
                       </div>
                       {isAssigned && (
                         <span className={styles.alreadyAssignedText}>Already Added</span>
                       )}
                     </div>
                   );
                 })}
               </div>
             ) : artistSearchQuery && !searchingArtists ? (
               <div className={styles.noResults}>No artists found</div>
             ) : null}
           </div>

           {/* Artists List */}
           <div className={styles.selectedMembersSection}>
             {selectedArtists.map(artist => renderMemberCard(artist, 'artist'))}
           </div>
         </div>
       );

     case STEPS.CREW_SELECTION:
       return (
         <div className={styles.formSection}>
           <h2 className={styles.sectionTitle}>Add Crew Members</h2>
           
           {/* Search Section */}
           <div className={styles.searchSection}>
             <div className={styles.searchBar}>
               <input
                 type="text"
                 placeholder="Search crew members by name..."
                 value={crewSearchQuery}
                 onChange={(e) => setCrewSearchQuery(e.target.value)}
                 onKeyDown={handleCrewSearchKeyDown}
                 className={styles.searchInput}
               />
               <button
                 className={styles.searchButton}
                 onClick={handleCrewSearch}
                 disabled={!crewSearchQuery.trim() || searchingCrew}
               >
                 <FontAwesomeIcon icon={faSearch} />
                 {searchingCrew ? 'Searching...' : 'Search'}
               </button>
             </div>
             
             {/* Search Results */}
             {searchingCrew ? (
               <div className={styles.noResults}>
                 Searching...
               </div>
             ) : crewResults.length > 0 ? (
               <div className={styles.searchResults}>
                 {crewResults.map((crew) => (
                   <div
                     key={crew.id}
                     className={`${styles.searchResult} ${(crew.isPending || crew.isAssigned) && styles.disabledSearchResult}`}
                     onClick={() => !crew.isPending && !crew.isAssigned && handleAddCrew(crew)}
                   >
                     <img
                       src={crew.profilePictureUrl || defaultAvatar}
                       alt={crew.fullName}
                       className={styles.profilePhoto}
                     />
                     <div className={styles.userInfo}>
                       <div className={styles.nameContainer}>
                         <span className={styles.fullName}>{crew.fullName}</span>
                         {crew.idVerificationStatus === 'Verified' && (
                           <img src={verifiedBadge} alt="Verified" className={styles.verifiedBadge} />
                         )}
                       </div>
                       <span className={styles.userName}>@{crew.userName}</span>
                       <span className={styles.userSubType}>{crew.userSubType}</span>
                     </div>
                     {(crew.isPending || crew.isAssigned) && (
                       <span className={styles.alreadyAssignedText}>
                         {crew.isPending ? "Pending Invitation" : "Already Assigned"}
                       </span>
                     )}
                   </div>
                 ))}
               </div>
             ) : crewSearchQuery && !searchingCrew ? (
               <div className={styles.noResults}>
                 No crew members found
               </div>
             ) : null}
           </div>

           {/* Crew Members List */}
           <div className={styles.selectedMembersSection}>
             {selectedCrew.map(crew => renderMemberCard(crew, 'crew'))}
           </div>
         </div>
       );

     // New Manager Selection Step
     case STEPS.MANAGER_SELECTION:
       return (
         <div className={styles.formSection}>
           <h2 className={styles.sectionTitle}>Add Managers</h2>
           <p className={styles.stepDescription}>
             Add managers to help with {eventType === 'tour' ? 'tour' : 'show'} coordination and management.
           </p>
           
           {/* Search Section */}
           <div className={styles.searchSection}>
             <div className={styles.searchBar}>
               <input
                 type="text"
                 placeholder="Search managers by name..."
                 value={managerSearchQuery}
                 onChange={(e) => setManagerSearchQuery(e.target.value)}
                 onKeyDown={(e) => e.key === 'Enter' && handleManagerSearch()}
                 className={styles.searchInput}
               />
               <button
                 className={styles.searchButton}
                 onClick={handleManagerSearch}
                 disabled={!managerSearchQuery.trim() || searchingManagers}
               >
                 <FontAwesomeIcon icon={faSearch} />
                 {searchingManagers ? 'Searching...' : 'Search'}
               </button>
             </div>
             
             {/* Search Results */}
             {searchingManagers ? (
               <div className={styles.noResults}>Searching...</div>
             ) : managerResults.length > 0 ? (
               <div className={styles.searchResults}>
                 {managerResults.map((manager) => {
                   const isAssigned = selectedManagers.some(m => m.userId === manager.id) ||
                                     (tourData?.managerDetails && Array.isArray(tourData.managerDetails) 
                                       ? tourData.managerDetails.some(m => m.userId === manager.id)
                                       : false);
                   
                   return (
                     <div
                       key={manager.id}
                       className={`${styles.searchResult} ${isAssigned && styles.disabledSearchResult}`}
                       onClick={() => !isAssigned && handleAddManager(manager)}
                     >
                       <img
                         src={manager.profilePictureUrl || defaultAvatar}
                         alt={manager.fullName}
                         className={styles.profilePhoto}
                       />
                       <div className={styles.userInfo}>
                         <div className={styles.nameContainer}>
                           <span className={styles.fullName}>{manager.fullName}</span>
                           {manager.idVerificationStatus === 'Verified' && (
                             <img src={verifiedBadge} alt="Verified" className={styles.verifiedBadge} />
                           )}
                         </div>
                         <span className={styles.userName}>@{manager.userName}</span>
                         <span className={styles.userSubType}>{manager.userSubType}</span>
                       </div>
                       {isAssigned && (
                         <span className={styles.alreadyAssignedText}>Already Added</span>
                       )}
                     </div>
                   );
                 })}
               </div>
             ) : managerSearchQuery && !searchingManagers ? (
               <div className={styles.noResults}>No managers found</div>
             ) : null}
           </div>

           {/* Managers List */}
           <div className={styles.selectedMembersSection}>
             {selectedManagers.map(manager => renderMemberCard(manager, 'manager'))}
           </div>
         </div>
       );

     // New Organization Selection Step
     case STEPS.ORGANIZATION_SELECTION:
       return (
         <div className={styles.formSection}>
           <h2 className={styles.sectionTitle}>Add Organizations</h2>
           <p className={styles.stepDescription}>
             Add organizations to oversee and manage this {eventType}.
           </p>
           
           {/* Search Section */}
           <div className={styles.searchSection}>
             <div className={styles.searchBar}>
               <input
                 type="text"
                 placeholder="Search organizations by name..."
                 value={organizationSearchQuery}
                 onChange={(e) => setOrganizationSearchQuery(e.target.value)}
                 onKeyDown={(e) => e.key === 'Enter' && handleOrganizationSearch()}
                 className={styles.searchInput}
               />
               <button
                 className={styles.searchButton}
                 onClick={handleOrganizationSearch}
                 disabled={!organizationSearchQuery.trim() || searchingOrganizations}
               >
                 <FontAwesomeIcon icon={faSearch} />
                 {searchingOrganizations ? 'Searching...' : 'Search'}
               </button>
             </div>
             
             {/* Search Results */}
             {searchingOrganizations ? (
               <div className={styles.noResults}>Searching...</div>
             ) : organizationResults.length > 0 ? (
               <div className={styles.searchResults}>
                 {organizationResults.map((organization) => {
                   const isAssigned = selectedOrganizations.some(o => o.userId === organization.id) ||
                                     (tourData?.organizationDetails && Array.isArray(tourData.organizationDetails) 
                                       ? tourData.organizationDetails.some(o => o.userId === organization.id)
                                       : false);
                   
                   return (
                     <div
                       key={organization.id}
                       className={`${styles.searchResult} ${isAssigned && styles.disabledSearchResult}`}
                       onClick={() => !isAssigned && handleAddOrganization(organization)}
                     >
                       <img
                         src={organization.profilePictureUrl || defaultOrgImage}
                         alt={organization.fullName}
                         className={styles.profilePhoto}
                       />
                       <div className={styles.userInfo}>
                         <div className={styles.nameContainer}>
                           <span className={styles.fullName}>{organization.fullName}</span>
                           {organization.idVerificationStatus === 'Verified' && (
                             <img src={verifiedBadge} alt="Verified" className={styles.verifiedBadge} />
                           )}
                         </div>
                         <span className={styles.userName}>@{organization.userName}</span>
                         <span className={styles.userSubType}>{organization.userSubType}</span>
                       </div>
                       {isAssigned && (
                         <span className={styles.alreadyAssignedText}>Already Added</span>
                       )}
                     </div>
                   );
                 })}
               </div>
             ) : organizationSearchQuery && !searchingOrganizations ? (
               <div className={styles.noResults}>No organizations found</div>
             ) : null}
           </div>

           {/* Organizations List */}
           <div className={styles.selectedMembersSection}>
             {selectedOrganizations.map(organization => renderMemberCard(organization, 'organization'))}
           </div>
         </div>
       );

     // New Permissions Step
     case STEPS.PERMISSIONS:
       return (
         <div className={styles.formSection}>
           <h2 className={styles.sectionTitle}>Permission Review</h2>
           <p className={styles.stepDescription}>
             Review permissions for assigned members. These settings can be changed later.
           </p>
           
           <div className={styles.permissionsReview}>
             {/* Creator Permission */}
             <div className={styles.permissionSection}>
               <h3>Your Permissions (Creator)</h3>
               <div className={styles.permissionRow}>
                 <FontAwesomeIcon icon={faLock} className={styles.permissionIcon} />
                 <div className={styles.permissionDetails}>
                   <span className={styles.permissionName}>Full Control</span>
                   <span className={styles.permissionDescription}>You have full administrative control over this {eventType}.</span>
                 </div>
               </div>
             </div>
             
             {/* Managers Permissions */}
             {selectedManagers.length > 0 && (
               <div className={styles.permissionSection}>
                 <h3>Manager Permissions</h3>
                 {selectedManagers.map(manager => {
                   const permissions = managerPermissions[manager.userId] || roleDefaultPermissions.manager;
                   const permissionType = JSON.stringify(permissions) === JSON.stringify(defaultPermissions.fullControl) ? 'Full Control' :
                                       JSON.stringify(permissions) === JSON.stringify(defaultPermissions.viewOnly) ? 'View Only' : 'Collaboration';
                   return (
                     <div key={manager.userId} className={styles.permissionRow}>
                       <img 
                         src={manager.profilePictureUrl || defaultAvatar} 
                         alt={manager.fullName}
                         className={styles.memberThumbnail}
                       />
                       <div className={styles.permissionDetails}>
                         <span className={styles.permissionName}>{manager.fullName} - {permissionType}</span>
                         <span className={styles.permissionDescription}>
                           {permissionType === 'Full Control' ? 'Can manage all aspects of this tour, including user assignments' :
                            permissionType === 'View Only' ? 'Can only view tour details without making changes' :
                            'Can manage schedule, tasks, and other operational details'}
                         </span>
                       </div>
                     </div>
                   );
                 })}
               </div>
             )}
             
             {/* Organizations Permissions */}
             {selectedOrganizations.length > 0 && (
               <div className={styles.permissionSection}>
                 <h3>Organization Permissions</h3>
                 {selectedOrganizations.map(org => {
                   const permissions = organizationPermissions[org.userId] || roleDefaultPermissions.organization;
                   const permissionType = JSON.stringify(permissions) === JSON.stringify(defaultPermissions.fullControl) ? 'Full Control' :
                                       JSON.stringify(permissions) === JSON.stringify(defaultPermissions.viewOnly) ? 'View Only' : 'Collaboration';
                   return (
                     <div key={org.userId} className={styles.permissionRow}>
                       <img 
                         src={org.profilePictureUrl || defaultOrgImage} 
                         alt={org.fullName}
                         className={styles.memberThumbnail}
                       />
                       <div className={styles.permissionDetails}>
                         <span className={styles.permissionName}>{org.fullName} - {permissionType}</span>
                         <span className={styles.permissionDescription}>
                           {permissionType === 'Full Control' ? 'Can manage all aspects of this tour, including user assignments' :
                            permissionType === 'View Only' ? 'Can only view tour details without making changes' :
                            'Can manage schedule, tasks, and other operational details'}
                         </span>
                       </div>
                     </div>
                   );
                 })}
               </div>
             )}
             
             {/* Artists and Crew Permissions */}
             <div className={styles.permissionSection}>
               <h3>Default Role Permissions</h3>
               <div className={styles.permissionRow}>
                 <FontAwesomeIcon icon={faMusic} className={styles.permissionIcon} />
                 <div className={styles.permissionDetails}>
                   <span className={styles.permissionName}>Artists</span>
                   <span className={styles.permissionDescription}>Can view schedule, edit setlist, manage guestlist</span>
                 </div>
               </div>
               <div className={styles.permissionRow}>
                 <FontAwesomeIcon icon={faUsers} className={styles.permissionIcon} />
                 <div className={styles.permissionDetails}>
                   <span className={styles.permissionName}>Crew</span>
                   <span className={styles.permissionDescription}>Can manage schedule, tasks, guestlist, hotels, and tickets</span>
                 </div>
               </div>
               <div className={styles.permissionRow}>
                 <FontAwesomeIcon icon={faMusic} className={styles.permissionIcon} />
                 <div className={styles.permissionDetails}>
                   <span className={styles.permissionName}>Bands</span>
                   <span className={styles.permissionDescription}>Can view schedule, edit setlist, manage guestlist</span>
                 </div>
               </div>
             </div>
           </div>
         </div>
       );
 
     default:
       return null;
   }
 };

 // Determine which steps to show based on the event type
 const getVisibleSteps = () => {
   // Always show Event Type and Tour Details steps
   const steps = [STEPS.EVENT_TYPE, STEPS.TOUR_DETAILS, STEPS.SELECT_DATES, STEPS.VENUE_DETAILS];
   
   // For both tour and show, add these steps
   steps.push(
     STEPS.BAND_SELECTION,
     STEPS.ARTIST_SELECTION,
     STEPS.CREW_SELECTION,
     STEPS.MANAGER_SELECTION,
     STEPS.ORGANIZATION_SELECTION,
     STEPS.PERMISSIONS
   );
   
   return steps;
 };

 // Function to get the display name for each step
 const getStepDisplayName = (step) => {
   switch (step) {
     case STEPS.EVENT_TYPE: return 'Event Type';
     case STEPS.TOUR_DETAILS: 
       return eventType === 'tour' ? 'Tour Details' : 'Show Details';
     case STEPS.SELECT_DATES: return 'Select Dates';
     case STEPS.VENUE_DETAILS: return 'Venue Details';
     case STEPS.BAND_SELECTION: return 'Bands';
     case STEPS.ARTIST_SELECTION: return 'Artists';
     case STEPS.CREW_SELECTION: return 'Crew';
     case STEPS.MANAGER_SELECTION: return 'Managers';
     case STEPS.ORGANIZATION_SELECTION: return 'Organizations';
     case STEPS.PERMISSIONS: return 'Permissions';
     default: return '';
   }
 };

 // Function to get the icon for each step
 const getStepIcon = (step) => {
   switch (step) {
     case STEPS.EVENT_TYPE: return faCalendar;
     case STEPS.TOUR_DETAILS: return faImage;
     case STEPS.SELECT_DATES: return faCalendar;
     case STEPS.VENUE_DETAILS: return faMapMarkerAlt;
     case STEPS.BAND_SELECTION: return faMusic;
     case STEPS.ARTIST_SELECTION: return faUsers;
     case STEPS.CREW_SELECTION: return faUsers;
     case STEPS.MANAGER_SELECTION: return faUserTie;
     case STEPS.ORGANIZATION_SELECTION: return faBuilding;
     case STEPS.PERMISSIONS: return faLock;
     default: return faCheck;
   }
 };
 
 // Create a component for the past date deselection modal
const PastDateModal = ({ show, date, onConfirm, onCancel }) => {
  if (!show || !date) return null;
  
  return (
    <div className={styles.modalOverlay} onClick={onCancel}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
        <h3 className={styles.modalTitle}>
          <FontAwesomeIcon icon={faExclamationTriangle} className={styles.warningIcon} />
          Past Date Warning
        </h3>
        <p className={styles.modalText}>
          You are removing a date that has already passed ({date.toLocaleDateString()}).
        </p>
        <p className={styles.modalSubText}>
          Please note that past dates cannot be selected again.
        </p>
        <div className={styles.modalActions}>
          <button className={styles.modalCancelButton} onClick={onCancel}>
            Keep Date
          </button>
          <button className={styles.modalConfirmButton} onClick={onConfirm}>
            Remove Date
          </button>
        </div>
      </div>
    </div>
  );
};

// Add this function to check if a date is in the past
const isDateInPast = (date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Set to start of day for comparison
  return date < today;
};

// Update the handleDateClick function to check for past dates
const handleDateClick = (date) => {
  const dateLimit = getDateLimit();
  
  // Create a new date in local timezone at start of day
  const normalizeDate = (d) => {
    // Extract year, month, day from the local date
    const year = d.getFullYear();
    const month = d.getMonth();
    const day = d.getDate();
    // Create new date with these components
    return new Date(year, month, day, 0, 0, 0, 0);
  };
  
  const clickedDate = normalizeDate(date);
  const dateKey = getDateKey(clickedDate);
  
  // Format date for checking assigned dates
  const dateStr = formatLocalDate(clickedDate);
  const assignedDate = userAssignedDates[dateStr];
  
  // Check if date is available (includes the check for current event)
  if (!isDateAvailable(clickedDate)) {
    showConflictModal(clickedDate);
    return;
  }
  
  // Compare dates using their local components instead of timestamps
  const compareDates = (d1, d2) => {
    return d1.getFullYear() === d2.getFullYear() &&
           d1.getMonth() === d2.getMonth() &&
           d1.getDate() === d2.getDate();
  };
  
  setSelectedDates(prev => {
    // For show type, allow only one date
    if (eventType === 'show' && prev.length >= 1) {
      // If the clicked date is already selected, check if it's in the past
      if (compareDates(normalizeDate(prev[0]), clickedDate)) {
        if (isDateInPast(prev[0])) {
          // If it's in the past, show the warning modal
          setPastDateToDeselect(prev[0]);
          setShowPastDateModal(true);
          return prev; // Don't change selected dates yet
        }
        
        // If not in the past, save venue before removing
        const prevDateKey = getDateKey(prev[0]);
        const currentVenue = Object.entries(venueDetails).find(([key, value]) => {
          const keyDate = new Date(parseInt(key));
          return compareDates(keyDate, prev[0]);
        });
        
        if (currentVenue && currentVenue[1]) {
          setRemovedVenueCache(cache => ({
            ...cache,
            [prevDateKey]: currentVenue[1]
          }));
        }
        
        return [];
      } else {
        return [clickedDate];
      }
    }
    
    // For tour type, handle multiple dates
    const dateExists = prev.some(d => compareDates(normalizeDate(d), clickedDate));
    
    if (dateExists) {
      // Find the existing date
      const existingDate = prev.find(d => compareDates(normalizeDate(d), clickedDate));
      
      // Check if deselecting a past date
      if (existingDate && isDateInPast(existingDate)) {
        // Show past date warning modal
        setPastDateToDeselect(existingDate);
        setShowPastDateModal(true);
        return prev; // Don't change selected dates yet
      }
      
      // If not in the past, save venue data to cache before removing the date
      if (existingDate) {
        const existingDateKey = getDateKey(existingDate);
        const currentVenue = Object.entries(venueDetails).find(([key, value]) => {
          const keyDate = new Date(parseInt(key));
          return compareDates(keyDate, existingDate);
        });
        
        if (currentVenue && currentVenue[1]) {
          setRemovedVenueCache(cache => ({
            ...cache,
            [existingDateKey]: currentVenue[1]
          }));
        }
      }
      
      // Remove date using local date comparison
      return prev.filter(d => !compareDates(normalizeDate(d), clickedDate));
    } else {
      if (prev.length >= dateLimit) {
        alert(`Your ${userData.membershipTier} membership allows up to ${dateLimit} dates.`);
        return prev;
      }
      return [...prev, clickedDate];
    }
  });
};

// Add these handlers for the past date modal
const handleConfirmPastDateDeselection = () => {
  if (!pastDateToDeselect) return;
  
  // Proceed with date deselection
  setSelectedDates(prev => {
    const normalizeDate = (d) => {
      const year = d.getFullYear();
      const month = d.getMonth();
      const day = d.getDate();
      return new Date(year, month, day, 0, 0, 0, 0);
    };
    
    const compareDates = (d1, d2) => {
      return d1.getFullYear() === d2.getFullYear() &&
             d1.getMonth() === d2.getMonth() &&
             d1.getDate() === d2.getDate();
    };
    
    // Remove the past date
    return prev.filter(d => !compareDates(normalizeDate(d), normalizeDate(pastDateToDeselect)));
  });
  
  // Cache venue data for the deselected date
  const dateKey = getDateKey(pastDateToDeselect);
  const currentVenue = Object.entries(venueDetails).find(([key, value]) => {
    const keyDate = new Date(parseInt(key));
    return keyDate.getFullYear() === pastDateToDeselect.getFullYear() &&
           keyDate.getMonth() === pastDateToDeselect.getMonth() &&
           keyDate.getDate() === pastDateToDeselect.getDate();
  });
  
  if (currentVenue && currentVenue[1]) {
    setRemovedVenueCache(cache => ({
      ...cache,
      [dateKey]: currentVenue[1]
    }));
  }
  
  // Reset state
  setPastDateToDeselect(null);
  setShowPastDateModal(false);
};

const handleCancelPastDateDeselection = () => {
  setPastDateToDeselect(null);
  setShowPastDateModal(false);
};

 // Get date key for venue storage
 const getDateKey = (date) => {
   if (!date) return '';
   // Use YYYY-MM-DD format as the key instead of timestamp
   // This ensures we get the same key even if the Date object is recreated
   return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
 };
 
 // Get the membership tier date limit
 const getDateLimit = () => {
   switch (userData?.membershipTier) {
     case 'Free': return 5;
     case 'Basic': return 10;
     case 'Pro': return 15;
     case 'Elite': return Infinity;
     default: return 5;
   }
 };
 
 return (
   <div className={styles.pageContainer}>
     <TopBar />
     <div className={styles.mainLayout}>
       <LeftMenu />
       <RightMenu />
       
       <div className={styles.content}>
         <div className={styles.container}>
           <h1 className={styles.title}>{getPageTitle()}</h1>
           
           <div className={styles.stepsContainer}>
             {getVisibleSteps().map((step) => (
               <div key={step} className={styles.stepWrapper}>
                 <div className={`${styles.step} ${styles[getStepStatus(step)]}`}>
                   <FontAwesomeIcon icon={getStepIcon(step)} />
                 </div>
                 <div className={styles.stepLabel}>
                   {getStepDisplayName(step)}
                 </div>
               </div>
             ))}
           </div>

           {renderStepContent()}

           <div className={styles.navigation}>
             {currentStep > STEPS.EVENT_TYPE && (
               <button
                 onClick={() => setCurrentStep(prev => prev - 1)}
                 className={`${styles.navButton} ${styles.prevButton}`}
                 disabled={loading}
               >
                 Previous
               </button>
             )}
             
             {currentStep < STEPS.PERMISSIONS ? (
               <button
                 onClick={() => {
                   if (currentStep === STEPS.VENUE_DETAILS && !allDatesHaveVenues()) {
                     setShowVenueWarningModal(true);
                   } else {
                     setCurrentStep(prev => prev + 1);
                   }
                 }}
                 className={`${styles.navButton} ${styles.nextButton}`}
                 disabled={
                   (currentStep === STEPS.TOUR_DETAILS && !isStep1Valid) ||
                   (currentStep === STEPS.SELECT_DATES && !isStep2Valid) ||
                   loading
                 }
               >
                 Next
               </button>
             ) : (
               <button
                 onClick={handleSubmit}
                 className={`${styles.navButton} ${styles.nextButton}`}
                 disabled={loading}
               >
                 {getSubmitButtonText()}
               </button>
             )}
           </div>

           {/* Venue warning modal */}
           <VenueWarningModal
             show={showVenueWarningModal}
             onConfirm={() => {
               setShowVenueWarningModal(false);
               setCurrentStep(prev => prev + 1);
             }}
             onCancel={() => setShowVenueWarningModal(false)}
           />
           
           {/* Date conflict modal */}
           <DateConflictModal
             show={showDateConflictModal}
             details={conflictDetails}
             onClose={() => setShowDateConflictModal(false)}
           />

           {/* Past Date Modal */}
<PastDateModal
  show={showPastDateModal}
  date={pastDateToDeselect}
  onConfirm={handleConfirmPastDateDeselection}
  onCancel={handleCancelPastDateDeselection}
/>
           
           {loading && <LoadingOverlay />}
         </div>
       </div>
     </div>
   </div>
 );
};

export default CreateTour;